import { useApi } from '../../hooks/useApi';
import { useCurrency } from '../../hooks/useCurrency';
import {
  Edit,
  Search,
  Trash2,
  UserPlus,
  Users,
  ChevronDown,
  Briefcase,
  Shield,
  CreditCard,
  Archive,
  MoreVertical,
  Lightbulb,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Sparkles,
  ShoppingCart,
  TrendingUp,
  Crown,
  Zap,
  Check,
  X,
  Upload,
  FileText,
  AlertTriangle,
  Plus,
  Info
} from 'lucide-react';
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { isCrmEnabledForOrg } from '../../modules/crm/utils/orgGating';
import { getAllStructurePositionNames, resolvePositionInput } from '../../utils/teamPositionMatch';
import { resolveRoleInput, roleImportHint } from '../../utils/bulkImportNormalize';
import PageAnimationWrapper from '../../components/PageAnimationWrapper';
import toast from 'react-hot-toast';
import { AntDropdown, AntMultiSelect, AntPagination } from '../../components/UI';
import { Modal, Card, InputNumber, Button, Badge, Typography, Space, Divider, Statistic, Alert, Table, Skeleton, Row, Col, Spin, Switch, Tooltip, ConfigProvider, theme as antTheme } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeInVariants, pageVariants, pageTransition } from '../../utils/animations';
import { openRazorpayCheckout } from '../../utils/razorpayCheckout';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import TeamHeader from './TeamHeader';
import TeamControls from './TeamControls';
import TeamMemberCard from './TeamMemberCard';
import './team.css';
const { Title, Text, Paragraph } = Typography;

const Team = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const api = useApi();
  // CRM module is org-gated — only show the CRM permission checkbox when the
  // super-admin has enabled CRM for this org (org.crmEnabled from billing/profile).
  // organization state is populated after fetchOrganization() resolves.
  const showCrmPermission = isCrmEnabledForOrg(organization);
  const [billingCurrency, setBillingCurrency] = useState(null);
  const { formatPrice, symbol, getPrice } = useCurrency({ billingCurrency });
  const { mode } = useTheme();
  const theme = mode || 'dark';
  const isDark = theme === 'dark';

  // Theme-aware styles
  const surfaceStyle = {
    background: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
    border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(15, 23, 42, 0.12)',
    boxShadow: isDark ? '0 25px 50px -12px rgba(0, 0, 0, 0.8)' : '0 12px 24px rgba(15, 23, 42, 0.12)',
    color: isDark ? '#e5e7eb' : '#0f172a',
  };

  const modalOverlayStyle = {
    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(12px)',
  };

  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // For reporting manager selection
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  // Position filter removed - backend handles position-based filtering automatically
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false); // Upgrade plan modal for free plan users
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null); // Selected plan for upgrade
  const [processingUpgradePlan, setProcessingUpgradePlan] = useState(null); // Currently processing upgrade plan
  const [upgradeMemberCount, setUpgradeMemberCount] = useState({}); // Store member count per plan: { 'standard': 1, 'premium': 1 }
  const [pendingUser, setPendingUser] = useState(null);
  const [additionalSlotsQuantity, setAdditionalSlotsQuantity] = useState(1); // Quantity of slots to purchase
  const [couponCode, setCouponCode] = useState(''); // Coupon code entered by user
  const [actualCouponId, setActualCouponId] = useState(null); // Actual coupon ID from Stripe
  const [couponValid, setCouponValid] = useState(null); // null = not checked, true = valid, false = invalid
  const [couponVerifying, setCouponVerifying] = useState(false); // Loading state for verification
  const [couponDiscount, setCouponDiscount] = useState(null); // Discount info
  const couponTimeoutRef = useRef(null); // For debouncing coupon verification
  const [organization, setOrganization] = useState(null);
  const [isAddPanelAnimating, setIsAddPanelAnimating] = useState(false);
  const [isEditPanelAnimating, setIsEditPanelAnimating] = useState(false);
  const [billingUpdateLoading, setBillingUpdateLoading] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [stealthModeModalVisible, setStealthModeModalVisible] = useState(false);
  const [userToToggleStealth, setUserToToggleStealth] = useState(null);
  const [stealthModeLoading, setStealthModeLoading] = useState(false);
  const [bulkStealthModeModalVisible, setBulkStealthModeModalVisible] = useState(false);
  const [bulkStealthModeLoading, setBulkStealthModeLoading] = useState(false);
  const [bulkStealthModeValue, setBulkStealthModeValue] = useState(null);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [invitationLink, setInvitationLink] = useState('');
  const [showInvitationLink, setShowInvitationLink] = useState(false);
  const [teamStructure, setTeamStructure] = useState([]);
  const [showPositionDropdown, setShowPositionDropdown] = useState(false);
  const [positionSearch, setPositionSearch] = useState('');
  const [teamStructureLoading, setTeamStructureLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [showPassword, setShowPassword] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [permissionsTooltip, setPermissionsTooltip] = useState({ show: false, userId: null, x: 0, y: 0 });
  const [isReportingManagersDropdownOpen, setIsReportingManagersDropdownOpen] = useState(false);
  const [isEditReportingManagersDropdownOpen, setIsEditReportingManagersDropdownOpen] = useState(false);
  
  // Bulk add mode and data
  const [addMode, setAddMode] = useState('single'); // 'single' or 'bulk'
  const [bulkUsers, setBulkUsers] = useState([
    {
      id: 1,
      firstName: '',
      lastName: '',
      email: '',
      role: 'org_user',
      phone: '',
      department: '',
      position: '',
      password: '',
      reportingManagers: [],
      permissions: {
        addProject: false,
        addTeamMember: false,
        viewReports: true,
        viewActivity: true,
        editProfile: true,
      },
      expanded: false,
    }
  ]);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  
  // CSV Upload states
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploadError, setCsvUploadError] = useState(null);
  const [csvUploadSuccess, setCsvUploadSuccess] = useState(false);
  const [isUploadingCsv, setIsUploadingCsv] = useState(false);
  
  // Bulk Users Table Pagination
  const [bulkUsersCurrentPage, setBulkUsersCurrentPage] = useState(1);
  const [bulkUsersPageSize, setBulkUsersPageSize] = useState(10);
  
  // Common values for "Apply to All" feature
  const [commonReportingManagers, setCommonReportingManagers] = useState([]);
  const [commonPermissions, setCommonPermissions] = useState({
    addProject: false,
    addTeamMember: false,
    viewReports: true,
    viewActivity: true,
    editProfile: true,
  });
  const [commonPosition, setCommonPosition] = useState('');
  const [commonRole, setCommonRole] = useState('org_user');
  const [commonPassword, setCommonPassword] = useState('');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'org_user',
    phone: '',
    department: '',
    position: '',
    reportingManager: '', // Keep for backward compatibility
    reportingManagers: [], // New array for multiple reporting managers
    billingEnabled: true,
    password: '',
    invitationMethod: 'manual', // 'manual', 'email', or 'link'
    sendEmailInvite: false,
    stealth_mode: false,
    permissions: {
      addProject: false,
      addTeamMember: false,
      viewReports: true,
      viewActivity: true,
      editProfile: true,
    },
  });

  // Form validation errors state - ordered by form field appearance
  const [formErrors, setFormErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    position: '',
    password: '',
    reportingManagers: '',
  });

  // Dynamic roles (fetched from organization settings)
  const [roles, setRoles] = useState([
    { value: 'org_user', label: 'Team Member', color: 'bg-blue-500/20 text-blue-400' },
    { value: 'org_admin', label: 'Organization Admin', color: 'bg-purple-500/20 text-purple-400' },
  ]);

  // Get positions from team structure
  const getPositionsFromTeamStructure = () => {
    if (teamStructure && teamStructure.length > 0) {
      // Extract all positions from team structure
      const allPositions = [];
      teamStructure.forEach(level => {
        if (level.positions && level.positions.length > 0) {
          level.positions.forEach(position => {
            allPositions.push({
              value: position.roleName,
              label: position.roleName,
              color: getPositionColor(position.roleName)
            });
          });
        }
      });
      return allPositions;
    }
    
    // Fallback to default positions if no team structure
    return [
      { value: 'CEO', label: 'CEO', color: 'bg-purple-500/20 text-purple-400' },
      { value: 'Manager', label: 'Manager', color: 'bg-blue-500/20 text-blue-400' },
      { value: 'Developer', label: 'Developer', color: 'bg-green-500/20 text-green-400' },
    ];
  };

  // Get accessible positions (lower levels only) for current user
  const getAccessiblePositions = () => {
    if (!teamStructure || !user.position || user.role === 'main_admin' || user.role === 'org_admin') {
      // Admins can assign any position
      return getPositionsFromTeamStructure();
    }

    // Find current user's level
    let currentUserLevel = -1;
    for (let i = 0; i < teamStructure.length; i++) {
      const level = teamStructure[i];
      if (level.positions && level.positions.some(pos => pos.roleName.trim() === user.position.trim())) {
        currentUserLevel = i;
        break;
      }
    }

    // If user's position not found, return empty array
    if (currentUserLevel === -1) {
      return [];
    }

    // Get positions at lower levels only
    const accessiblePositions = [];
    for (let i = currentUserLevel + 1; i < teamStructure.length; i++) {
      const level = teamStructure[i];
      if (level.positions) {
        level.positions.forEach(position => {
          accessiblePositions.push({
            value: position.roleName,
            label: position.roleName,
            color: getPositionColor(position.roleName)
          });
        });
      }
    }

    return accessiblePositions;
  };

  // Function to assign different colors to positions
  const getPositionColor = (positionName) => {
    const colorMap = {
      'CEO': 'bg-purple-500/20 text-purple-400',
      'CTO': 'bg-indigo-500/20 text-indigo-400',
      'Manager': 'bg-blue-500/20 text-blue-400',
      'Team Lead': 'bg-cyan-500/20 text-cyan-400',
      'Senior Developer': 'bg-green-500/20 text-green-400',
      'Sr. React Developer': 'bg-emerald-500/20 text-emerald-400',
      'React Developer': 'bg-teal-500/20 text-teal-400',
      'Developer': 'bg-green-500/20 text-green-400',
      'Frontend Developer': 'bg-blue-500/20 text-blue-400',
      'Backend Developer': 'bg-orange-500/20 text-orange-400',
      'Full Stack Developer': 'bg-pink-500/20 text-pink-400',
      'UI/UX Designer': 'bg-rose-500/20 text-rose-400',
      'Product Manager': 'bg-violet-500/20 text-violet-400',
      'Project Manager': 'bg-amber-500/20 text-amber-400',
      'QA Engineer': 'bg-lime-500/20 text-lime-400',
      'DevOps Engineer': 'bg-sky-500/20 text-sky-400',
      'Data Scientist': 'bg-fuchsia-500/20 text-fuchsia-400',
      'Analyst': 'bg-yellow-500/20 text-yellow-400',
      'Intern': 'bg-gray-500/20 text-gray-400',
    };

    // Return specific color if found, otherwise generate a consistent color based on position name
    if (colorMap[positionName]) {
      return colorMap[positionName];
    }

    // Generate consistent color for unknown positions using hash of position name
    const hash = positionName.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    const colors = [
      'bg-red-500/20 text-red-400',
      'bg-orange-500/20 text-orange-400',
      'bg-yellow-500/20 text-yellow-400',
      'bg-lime-500/20 text-lime-400',
      'bg-green-500/20 text-green-400',
      'bg-emerald-500/20 text-emerald-400',
      'bg-teal-500/20 text-teal-400',
      'bg-cyan-500/20 text-cyan-400',
      'bg-sky-500/20 text-sky-400',
      'bg-blue-500/20 text-blue-400',
      'bg-indigo-500/20 text-indigo-400',
      'bg-violet-500/20 text-violet-400',
      'bg-purple-500/20 text-purple-400',
      'bg-fuchsia-500/20 text-fuchsia-400',
      'bg-pink-500/20 text-pink-400',
      'bg-rose-500/20 text-rose-400',
    ];
    
    return colors[Math.abs(hash) % colors.length];
  };

  const positions = getPositionsFromTeamStructure();



  const fetchTeamStructure = useCallback(async () => {
    try {
      setTeamStructureLoading(true);
      const response = await api.get('/api/v1/organizations/team-structure');
      if (response.data.success) {
        setTeamStructure(response.data.data.teamStructure || []);
      }
    } catch (error) {
      console.error('Error fetching team structure:', error);
    } finally {
      setTeamStructureLoading(false);
    }
  }, []);

  const fetchOrganizationRoles = useCallback(async () => {
    try {
      // Try to fetch custom roles from organization settings
      const response = await api.get('/api/v1/organizations/roles');
      if (response.data.success) {
        // Filter out System Admin role
        const filteredRoles = response.data.data.roles.filter(role => role.value !== 'main_admin');
        setRoles(filteredRoles);
      }
    } catch (error) {
      // If custom roles endpoint doesn't exist, use default system roles
      console.log('Using default system roles');
      setRoles([
        { value: 'org_user', label: 'Team Member', color: 'bg-blue-500/20 text-blue-400' },
        { value: 'org_admin', label: 'Organization Admin', color: 'bg-purple-500/20 text-purple-400' },
        // { value: 'main_admin', label: 'System Admin', color: 'bg-red-500/20 text-red-400' },
      ]);
    }
  }, []);

  const fetchOrganization = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/billing/profile');
      if (response.data.success) {
        setOrganization(response.data.data);
        const d = response.data.data;
        setBillingCurrency(d.orgPlanCurrency || (d.paymentGateway === 'razorpay' ? 'INR' : 'USD'));
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
    }
  }, []);

  // Re-verify coupon when organization changes (member count changes)
  useEffect(() => {
    if (couponCode && couponCode.trim() !== '' && couponValid !== null && organization) {
      // Re-verify coupon when organization/member count changes
      if (couponTimeoutRef.current) {
        clearTimeout(couponTimeoutRef.current);
      }
      
      couponTimeoutRef.current = setTimeout(() => {
        verifyCouponCode(couponCode);
      }, 500); // Debounce for 500ms
    }
    
    return () => {
      if (couponTimeoutRef.current) {
        clearTimeout(couponTimeoutRef.current);
      }
    };
  }, [organization?.usedMemberSlots, organization?.paidMemberSlots]); // Re-verify when member count changes

  // Verify coupon code
  const verifyCouponCode = async (code) => {
    if (!code || code.trim() === '') {
      setActualCouponId(null);
      setCouponValid(null);
      setCouponDiscount(null);
      return;
    }

    setCouponVerifying(true);
    try {
      // Get current member count (total slots = admin + members)
      let totalSeats = undefined;
      if (organization) {
        // Use current slots (includes admin)
        totalSeats = organization.paidMemberSlots || organization.usedMemberSlots || undefined;
      }
      
      const isRazorpay = organization?.paymentGateway === 'razorpay';
      const verifyUrl = isRazorpay
        ? '/api/v1/public/razorpay-billing/verify-coupon'
        : '/api/v1/public/billing/verify-coupon';

      const response = await api.post(verifyUrl, {
        couponCode: code.trim(),
        memberCount: totalSeats,
        organizationId: organization?._id,
      });

      if (response.data.success && response.data.data.valid) {
        setCouponValid(true);
        setCouponDiscount(response.data.data.discount);
        setActualCouponId(response.data.data.couponCode);
        toast.success(`Coupon applied! ${response.data.data.discount}`);
      } else {
        setCouponValid(false);
        setCouponDiscount(null);
        setActualCouponId(null);
        toast.error(response.data.message || 'Invalid coupon code');
      }
    } catch (error) {
      setCouponValid(false);
      setCouponDiscount(null);
      setActualCouponId(null);
      toast.error(error.response?.data?.message || 'Failed to verify coupon');
    } finally {
      setCouponVerifying(false);
    }
  };

  // Handle upgrade plan selection (for free plan users)
  const handleUpgradePlanSelection = async (plan) => {
    setSelectedUpgradePlan(plan);
    setProcessingUpgradePlan(plan);
    
    try {
      if (!user?.id) {
        toast.error('User not found. Please try again.');
        setProcessingUpgradePlan(null);
        return;
      }

      // Calculate minimum member count (current used - 1 for admin)
      const currentSlots = organization?.usedMemberSlots || 5; // All current members including admin
      const minMemberCount = Math.max(1, currentSlots - 1); // Minimum = current used - 1 (admin excluded)
      
      // Get member count for this plan (default to minimum if not set)
      const memberCount = upgradeMemberCount[plan] || minMemberCount;
      
      // Calculate total seats and price
      const totalSeats = memberCount + 1; // +1 for admin
      const planAmount = getPrice(plan);
      const totalAmount = totalSeats * planAmount;

      console.log('🔄 Upgrade Checkout Calculation:', {
        currentPaidSlots: organization?.paidMemberSlots,
        currentUsedSlots: organization?.usedMemberSlots,
        currentSlots: currentSlots,
        breakdown: `${totalSeats} total slots = 1 admin + ${memberCount} members`,
        memberCount: memberCount,
        totalSeats: totalSeats,
        plan: plan,
        planAmount: planAmount,
        totalAmount: totalAmount,
        calculation: `${totalSeats} seats × ${symbol}${planAmount} = ${symbol}${totalAmount}/month`,
        note: 'Upgrading from free plan: User can add additional members'
      });

      toast.loading(`Creating checkout for ${totalSeats} seats (${memberCount} members + 1 admin)...`, { id: 'upgrade-loading' });

      // Try Razorpay for Indian users
      const isRazorpay = organization?.paymentGateway === 'razorpay';
      if (isRazorpay) {
        try {
          const rzpRes = await api.post('/api/v1/public/razorpay-billing/organization-subscription-checkout', {
            userId: user.id, plan, memberCount,
            ...(actualCouponId && { couponCode: actualCouponId })
          });
          if (rzpRes.data?.success && (rzpRes.data?.data?.subscriptionId || rzpRes.data?.data?.orderId)) {
            toast.dismiss('upgrade-loading');
            const rzpData = rzpRes.data.data;
            openRazorpayCheckout({
              razorpayKeyId: rzpData.razorpayKeyId, amount: rzpData.amount,
              currency: rzpData.currency, orderId: rzpData.orderId,
              subscriptionId: rzpData.subscriptionId,
              customerName: rzpData.customerName, customerEmail: rzpData.customerEmail,
              notes: rzpData.notes, description: `TeamGrid ${plan} Upgrade`,
              onSuccess: async (pr) => {
                try {
                  await api.post('/api/v1/public/razorpay-billing/verify-payment', {
                    razorpay_order_id: pr.razorpay_order_id, razorpay_payment_id: pr.razorpay_payment_id,
                    razorpay_signature: pr.razorpay_signature, razorpay_subscription_id: pr.razorpay_subscription_id,
                    userId: user.id,
                    organizationId: rzpData.notes.organizationId, plan, memberCount,
                    totalSeats: rzpData.totalSeats, type: rzpData.billingType === 'one_time' ? 'one_time_payment' : 'organization_subscription',
                  });
                  localStorage.setItem('showSlotPurchaseAfterUpgrade', 'true');
                  toast.success('Upgrade successful!');
                  setTimeout(() => { window.location.href = '/dashboard?payment=success'; }, 100);
                } catch { toast.error('Payment verification failed'); setProcessingUpgradePlan(null); }
              },
              onFailure: () => { toast.error('Payment failed'); setProcessingUpgradePlan(null); },
              onDismiss: () => { setProcessingUpgradePlan(null); },
            });
            return;
          }
        } catch { /* fall through to Stripe */ }
      }

      // Stripe flow
      const response = await api.post('/api/v1/public/billing/organization-subscription-checkout', {
        userId: user.id,
        plan: plan,
        memberCount: memberCount,
        ...(actualCouponId && { couponCode: actualCouponId })
      });

      if (response.data?.success) {
        toast.dismiss('upgrade-loading');
        const url = response.data?.data?.url;
        if (url) {
          localStorage.setItem('showSlotPurchaseAfterUpgrade', 'true');
          window.location.href = url;
        } else {
          toast.error('Checkout URL not received');
          setProcessingUpgradePlan(null);
        }
      } else {
        toast.dismiss('upgrade-loading');
        toast.error(response.data?.message || 'Failed to start checkout');
        setProcessingUpgradePlan(null);
      }
    } catch (error) {
      console.error('Upgrade plan selection error:', error);
      toast.error(error.response?.data?.message || 'Failed to process upgrade');
      setProcessingUpgradePlan(null);
    }
  };

  const handlePaymentSubmit = async () => {
    if (!pendingUser) return;

    try {
      setLoading(true);
      
      // Get organization's locked plan
      const orgPlan = organization?.orgPlanSelection || 'standard';
      
      const isRazorpay = organization?.paymentGateway === 'razorpay';

      if (pendingUser.needsAdditionalSlots) {
        if (additionalSlotsQuantity < 1 || additionalSlotsQuantity > 100) {
          toast.error('Please enter a valid quantity (1-100)');
          setLoading(false);
          return;
        }

        if (isRazorpay) {
          // Razorpay flow for purchasing additional slots
          try {
            const rzpRes = await api.post('/api/v1/razorpay-billing/purchase-additional-slots', {
              userId: pendingUser.id || user.id,
              additionalSlots: additionalSlotsQuantity,
              ...(actualCouponId && { couponCode: actualCouponId })
            });
            if (rzpRes.data?.success && rzpRes.data?.data?.orderId) {
              const rzpData = rzpRes.data.data;
              openRazorpayCheckout({
                razorpayKeyId: rzpData.razorpayKeyId, amount: rzpData.amount,
                currency: rzpData.currency, orderId: rzpData.orderId,
                customerName: user?.firstName + ' ' + user?.lastName,
                customerEmail: user?.email,
                notes: rzpData.notes, description: `TeamGrid Additional Slots`,
                onSuccess: async (pr) => {
                  try {
                    await api.post('/api/v1/razorpay-billing/verify-payment', {
                      razorpay_order_id: pr.razorpay_order_id, razorpay_payment_id: pr.razorpay_payment_id,
                      razorpay_signature: pr.razorpay_signature,
                      type: 'additional_slots', additionalSlots: additionalSlotsQuantity,
                    });
                    toast.success('Slots purchased!');
                    setTimeout(() => { window.location.reload(); }, 500);
                  } catch { toast.error('Payment verification failed'); setLoading(false); }
                },
                onFailure: () => { toast.error('Payment failed'); setLoading(false); },
                onDismiss: () => { setLoading(false); },
              });
              return;
            }
          } catch { /* fall through to Stripe */ }
        }

        const response = await api.post('/api/v1/billing/purchase-additional-slots', {
          userId: pendingUser.id || user.id,
          additionalSlots: additionalSlotsQuantity,
          ...(actualCouponId && { couponCode: actualCouponId })
        });
        
        if (response.data.success) {
          window.location.href = response.data.data.url;
        } else {
          toast.error('Failed to create checkout session');
          setLoading(false);
        }
      } else {
        // Legacy: Per-user subscription
        if (isRazorpay) {
          try {
            const rzpRes = await api.post('/api/v1/public/razorpay-billing/user-subscription-checkout', {
              userId: pendingUser.id, plan: orgPlan,
              ...(actualCouponId && { couponCode: actualCouponId })
            });
            if (rzpRes.data?.success && (rzpRes.data?.data?.subscriptionId || rzpRes.data?.data?.orderId)) {
              const rzpData = rzpRes.data.data;
              openRazorpayCheckout({
                razorpayKeyId: rzpData.razorpayKeyId,
                subscriptionId: rzpData.subscriptionId,
                orderId: rzpData.orderId,
                amount: rzpData.amount, currency: rzpData.currency,
                customerName: pendingUser.firstName + ' ' + pendingUser.lastName,
                customerEmail: pendingUser.email,
                notes: rzpData.notes, description: `TeamGrid ${orgPlan} Plan`,
                onSuccess: async (pr) => {
                  try {
                    await api.post('/api/v1/public/razorpay-billing/verify-payment', {
                      razorpay_order_id: pr.razorpay_order_id,
                      razorpay_subscription_id: pr.razorpay_subscription_id,
                      razorpay_payment_id: pr.razorpay_payment_id,
                      razorpay_signature: pr.razorpay_signature,
                      userId: pendingUser.id, plan: orgPlan, totalSeats: '1', type: rzpData.billingType === 'one_time' ? 'one_time_payment' : 'user_subscription',
                    });
                    toast.success('Payment successful!');
                    setTimeout(() => { window.location.reload(); }, 500);
                  } catch { toast.error('Payment verification failed'); setLoading(false); }
                },
                onFailure: () => { toast.error('Payment failed'); setLoading(false); },
                onDismiss: () => { setLoading(false); },
              });
              return;
            }
          } catch { /* fall through to Stripe */ }
        }

        const response = await api.post('/api/v1/billing/user-subscription-checkout', {
          userId: pendingUser.id, plan: orgPlan,
          ...(actualCouponId && { couponCode: actualCouponId })
        });
      
        if (response.data.success) {
          window.location.href = response.data.data.url;
        } else {
          toast.error('Failed to create checkout session');
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.message || 'Failed to process payment');
      setLoading(false);
    }
  };

  const fetchAllUsers = useCallback(async () => {
    try {
      // Fetch all users without position filtering for reporting manager selection
      const response = await api.get('/api/v1/users/all');
      setAllUsers(response.data.data.users);
    } catch (error) {
      console.error('Error fetching all users:', error);
      setAllUsers([]);
    }
  }, []); // Remove api dependency - useApi hook provides stable reference

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
      });

      // Add search parameter if search term exists
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add role filter if selected
      if (roleFilter) {
        params.append('role', roleFilter);
      }

      // Position filter removed - backend handles position-based filtering automatically



      const response = await api.get(`/api/v1/users?${params.toString()}`);
      setUsers(response.data.data.users);
      setTotalUsers(response.data.data.pagination.total);
      setTotalPages(response.data.data.pagination.pages);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, roleFilter]);

  // Check for payment success after upgrade (just refresh, don't show modal automatically)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const orgId = urlParams.get('orgId');

    if (payment === 'success' && orgId) {
      // Check if this was an upgrade
      const showSlotPurchase = localStorage.getItem('showSlotPurchaseAfterUpgrade');
      
      if (showSlotPurchase === 'true') {
        // Clear the flag
        localStorage.removeItem('showSlotPurchaseAfterUpgrade');
        
        // Just refresh organization data (don't show modal automatically)
        // Modal will show when user clicks "Add Member" if slots are exhausted
        fetchOrganization().then(() => {
          toast.success('Plan upgraded successfully! You can now add more members.');
        });
      } else {
        // Regular payment success - just refresh
        fetchOrganization();
        fetchUsers();
      }
      
      // Clean up URL parameters
      urlParams.delete('payment');
      urlParams.delete('orgId');
      window.history.replaceState({}, document.title, `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`);
    }
  }, [fetchOrganization, fetchUsers]);

  // Fetch users when pagination/filters change
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]); // Re-runs when fetchUsers changes (when currentPage, pageSize, searchTerm, or roleFilter changes)

  // Fetch initial data once on mount
  useEffect(() => {
    fetchAllUsers(); // Fetch all users for reporting manager selection
    fetchTeamStructure();
    fetchOrganizationRoles();
    fetchOrganization();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close position dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPositionDropdown && !event.target.closest('.position-dropdown')) {
        setShowPositionDropdown(false);
        setPositionSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPositionDropdown]);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleRoleFilterChange = (value) => {
    setRoleFilter(value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Position filter removed - backend handles position-based filtering automatically



  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setCurrentPage(1);
  };

  const handlePermissionsClick = (e, userItem) => {
    e.preventDefault();
    const rect = e.target.getBoundingClientRect();
    setPermissionsTooltip({
      show: true,
      userId: userItem._id,
      x: rect.left,
      y: rect.bottom + 5
    });
  };

  const closePermissionsTooltip = () => {
    setPermissionsTooltip({ show: false, userId: null, x: 0, y: 0 });
  };

  const getPermissionsCount = (userItem) => {
    if (userItem.role === 'org_admin') return 'All Access';
    
    if (!userItem.permissions) return '0 Permissions';
    
    const count = Object.values(userItem.permissions).filter(Boolean).length;
    return `${count} Permissions`;
  };

  const getPermissionsList = (userItem) => {
    if (userItem.role === 'org_admin') return ['All Access'];
    
    if (!userItem.permissions) return ['No permissions'];
    
    const permissions = [];
    if (userItem.permissions.addProject) permissions.push('Add Project');
    if (userItem.permissions.addTeamMember) permissions.push('Add Team');
    if (userItem.permissions.viewReports) permissions.push('Reports');
    if (userItem.permissions.viewActivity) permissions.push('Activity');
    if (userItem.permissions.editProfile) permissions.push('Edit Profile');
    // CRM badge only shown if the org has CRM enabled — otherwise listing it
    // confuses admins of orgs that don't have CRM yet.
    if (userItem.permissions.crmAccess && showCrmPermission) permissions.push('CRM');

    return permissions.length > 0 ? permissions : ['No permissions'];
  };

  // Validate form fields in order (as they appear in the form)
  const validateForm = (data, invitationMethod) => {
    const errors = {
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      position: '',
      password: '',
    };

    // Validate in order of form appearance
    // 1. First Name
    if (!data.firstName || data.firstName.trim() === '') {
      errors.firstName = 'First name is required';
    } else if (data.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    } else if (data.firstName.trim().length > 50) {
      errors.firstName = 'First name must be less than 50 characters';
    }

    // 2. Last Name
    if (!data.lastName || data.lastName.trim() === '') {
      errors.lastName = 'Last name is required';
    } else if (data.lastName.trim().length < 1) {
      errors.lastName = 'Last name is required';
    } else if (data.lastName.trim().length > 50) {
      errors.lastName = 'Last name must be less than 50 characters';
    }

    // 3. Email
    if (!data.email || data.email.trim() === '') {
      errors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email.trim())) {
        errors.email = 'Please provide a valid email address';
      }
    }

    // 4. Role - usually has a default value, but validate anyway
    if (!data.role || data.role.trim() === '') {
      errors.role = 'Role is required';
    }

    // 5. Position - optional but validate if provided
    // (Position is optional, so we don't require it)

    // 6. Password (only required for manual invitation)
    if (invitationMethod === 'manual') {
      if (!data.password || data.password.trim() === '') {
        errors.password = 'Password is required for manual user creation';
      } else if (data.password.length < 8) {
        errors.password = 'Password must be at least 8 characters long';
      }
    }

    return errors;
  };

  // Parse backend validation errors and map to form fields
  const parseBackendErrors = (errorResponse) => {
    const errors = {
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      position: '',
      password: '',
    };

    if (!errorResponse) return errors;

    // Check for validation error details (array format)
    if (errorResponse.data?.error?.details && Array.isArray(errorResponse.data.error.details)) {
      const details = errorResponse.data.error.details;
      details.forEach((detail) => {
        const detailStr = detail.toLowerCase();
        if (detailStr.includes('first name') || detailStr.includes('firstname')) {
          errors.firstName = detail;
        } else if (detailStr.includes('last name') || detailStr.includes('lastname')) {
          errors.lastName = detail;
        } else if (detailStr.includes('email')) {
          errors.email = detail;
        } else if (detailStr.includes('role')) {
          errors.role = detail;
        } else if (detailStr.includes('position')) {
          errors.position = detail;
        } else if (detailStr.includes('password')) {
          errors.password = detail;
        }
      });
    }
    // Check for validation errors (legacy format)
    else if (errorResponse.data?.errors && Array.isArray(errorResponse.data.errors)) {
      errorResponse.data.errors.forEach((errorMsg) => {
        const errorStr = errorMsg.toLowerCase();
        if (errorStr.includes('first name') || errorStr.includes('firstname')) {
          errors.firstName = errorMsg;
        } else if (errorStr.includes('last name') || errorStr.includes('lastname')) {
          errors.lastName = errorMsg;
        } else if (errorStr.includes('email')) {
          errors.email = errorMsg;
        } else if (errorStr.includes('role')) {
          errors.role = errorMsg;
        } else if (errorStr.includes('position')) {
          errors.position = errorMsg;
        } else if (errorStr.includes('password')) {
          errors.password = errorMsg;
        }
      });
    }
    // Check for field-specific errors in response
    else if (errorResponse.data?.error) {
      const errorData = errorResponse.data.error;
      if (errorData.firstName) errors.firstName = errorData.firstName;
      if (errorData.lastName) errors.lastName = errorData.lastName;
      if (errorData.email) errors.email = errorData.email;
      if (errorData.role) errors.role = errorData.role;
      if (errorData.position) errors.position = errorData.position;
      if (errorData.password) errors.password = errorData.password;
    }

    return errors;
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserLoading(true);
    
    // Clear previous errors
    setFormErrors({
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      position: '',
      password: '',
    });

    try {
      // Extract the organization ID - handle both string and object cases
      let organizationId;
      if (typeof user.organizationId === 'object') {
        organizationId = user.organizationId._id || user.organizationId.id;
      } else {
        organizationId = user.organizationId;
      }

      // Client-side validation
      const validationErrors = validateForm(formData, formData.invitationMethod);
      const hasErrors = Object.values(validationErrors).some(error => error !== '');
      
      if (hasErrors) {
        setFormErrors(validationErrors);
        setAddUserLoading(false);
        // Scroll to first error
        const firstErrorField = Object.keys(validationErrors).find(key => validationErrors[key] !== '');
        if (firstErrorField) {
          const element = document.querySelector(`[data-field="${firstErrorField}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
        }
        return;
      }

      // Position-first guard: if position is blank, show "Please add a position"
      // before the backend can return the confusing "Valid reporting manager ID
      // is required" toast. org_admin sits at top of hierarchy, no position needed.
      if (formData.role !== 'org_admin' && (!formData.position || String(formData.position).trim() === '')) {
        toast.error('Please add a position first');
        setAddUserLoading(false);
        return;
      }

      const requestData = {
        ...formData,
        organizationId: organizationId,
      };

      // Remove invitationMethod and sendEmailInvite from request if not needed
      const { invitationMethod, sendEmailInvite, password, ...userData } = requestData;
      
      // Handle multiple reporting managers - send both formats for backward compatibility
      if (userData.reportingManagers && userData.reportingManagers.length > 0) {
        // If we have multiple reporting managers, use the first one as the primary reportingManager
        userData.reportingManager = userData.reportingManagers[0];
        // Keep the reportingManagers array for the new format
      } else if (userData.reportingManager && !userData.reportingManagers) {
        // If we only have a single reportingManager, convert it to array format
        userData.reportingManagers = [userData.reportingManager];
      }

      // For manual invitation, password is required
      if (invitationMethod === 'manual') {
        if (!password || password.trim() === '') {
          setFormErrors(prev => ({ ...prev, password: 'Password is required for manual user creation' }));
          setAddUserLoading(false);
          return;
        }
        userData.password = password;
      }
      // For email invitation, password is not needed (user will set it themselves)

      console.log('Creating user with reporting managers:', {
        reportingManager: userData.reportingManager,
        reportingManagers: userData.reportingManagers
      });

      const response = await api.post('/api/v1/users', userData);
      const userId = response.data.data.user.id;
      
      // Check if organization is on free plan
      const isFreePlan = organization?.orgPlanSelection === 'free';
      
      // Check slots for both paid and free plans
      const slotsInfo = response.data.data.slots; // Backend returns this
      const isPaidPlan = organization?.orgPlanSelection === 'standard' || organization?.orgPlanSelection === 'premium';
      const hasAvailableSlots = (isPaidPlan || isFreePlan) && slotsInfo && slotsInfo.remaining > 0;
      
      if (isFreePlan && hasAvailableSlots) {
        // Free plan with available slots - user auto-activated
        toast.success(`User added successfully! ${slotsInfo.remaining} free slot(s) remaining.`);
      } else if (isPaidPlan && hasAvailableSlots) {
        // Paid plan with available slots - user auto-activated (no payment needed)
        toast.success(`User added successfully! ${slotsInfo.remaining} slots remaining.`);
      } else if ((isPaidPlan || isFreePlan) && slotsInfo && slotsInfo.remaining === 0) {
        // Plan but slots exhausted AFTER adding this user - user created but needs payment to activate
        const planType = isFreePlan ? 'free' : 'paid';
        toast.success(isFreePlan 
          ? 'User created successfully! All 5 free slots are now used. Purchase additional slots to activate this user.'
          : 'User created successfully! All slots are now used. Purchase additional slots to activate this user.'
        );
        
        // Don't show payment modal immediately - only show when trying to add NEXT user
        // Store user info for potential future payment (if admin wants to activate this user)
      setPendingUser({
        id: userId,
        name: `${userData.firstName} ${userData.lastName}`,
          email: userData.email,
          needsAdditionalSlots: true // Flag to indicate this is for additional slots
        });
        setAdditionalSlotsQuantity(1); // Reset to 1 slot
      }
      
      // Refresh organization data to update slots count
      await fetchOrganization();
      fetchUsers();
      fetchAllUsers();

      // Close add modal
      setShowAddModal(false);
      
      if (invitationMethod === 'email' && sendEmailInvite) {
        // Send invitation email
        try {
          await api.post('/api/v1/users/send-invitation-email', {
            userId: response.data.data.user._id,
            email: formData.email,
            organizationId: organizationId
          });
          toast.success('Invitation email sent!');
        } catch (error) {
          console.error('Error sending invitation email:', error);
          toast.error('Failed to send invitation email');
        }
      } else if (invitationMethod === 'link') {
        // Generate organization invitation link
        try {
          const inviteResponse = await api.post('/api/v1/organizations/invitation-link', {
            organizationId: organizationId,
            email: formData.email
          });
          setInvitationLink(inviteResponse.data.data.invitationLink);
          setShowInvitationLink(true);
          return; // Don't show payment modal for link invitations
        } catch (error) {
          console.error('Error generating invitation link:', error);
          toast.error('Failed to generate invitation link');
        }
      }
      
      // DON'T show payment modal immediately after adding user
      // Modal will show in openAddPanel when trying to add NEXT user if slots are exhausted

      // Reset form data
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        role: 'org_user',
        phone: '',
        department: '',
        position: '',
        reportingManager: '',
        billingEnabled: true,
        password: '',
        invitationMethod: 'manual',
        sendEmailInvite: false,
        permissions: {
          addProject: false,
          addTeamMember: false,
          viewReports: true,
          viewActivity: true,
          editProfile: true,
        },
      });
      setShowPassword(false);
      // Clear form errors on success
      setFormErrors({
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        position: '',
        password: '',
        reportingManagers: '',
      });

      if (invitationMethod === 'email') {
        fetchUsers();
      }
    } catch (error) {
      console.error('Error adding user:', error);
      console.error('Failed to save reporting managers for new user');
      
      if (error.response) {
        console.error('Error response:', error.response.data);
        
        // Parse backend errors and set form errors
        const backendErrors = parseBackendErrors(error.response);
        setFormErrors(backendErrors);
        
        // Check if we have field-specific errors
        const hasFieldErrors = Object.values(backendErrors).some(error => error !== '');
        
        if (hasFieldErrors) {
          // Scroll to first error field
          const firstErrorField = Object.keys(backendErrors).find(key => backendErrors[key] !== '');
          if (firstErrorField) {
            setTimeout(() => {
              const element = document.querySelector(`[data-field="${firstErrorField}"]`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.focus();
              }
            }, 100);
          }
        }
        
        // Also show toast for general errors
        let errorMessage = 'Failed to add user. Please check the form and try again.';

        // Check for validation error details (array format)
        if (error.response.data.error?.details && Array.isArray(error.response.data.error.details) && error.response.data.error.details.length > 0) {
          errorMessage = error.response.data.error.details.join('. ');
        }
        // Check for validation errors (legacy format)
        else if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          errorMessage = 'Validation errors: ' + error.response.data.errors.join(', ');
        }
        // Check for error message
        else if (error.response.data.error?.message) {
          errorMessage = error.response.data.error.message;
        }
        // Check for top-level message
        else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        
        // Only show toast if there are no field-specific errors (to avoid duplicate messages)
        if (!hasFieldErrors) {
          toast.error(errorMessage);
        }
      } else {
        // For non-response errors (like network errors)
        const errorMessage = error.message || 'Network error. Please try again.';
        toast.error(errorMessage);
      }
    } finally {
      setAddUserLoading(false);
    }
  };

  // ========== BULK ADD FUNCTIONS ==========
  
  // Add new row to bulk users table
  const addBulkUserRow = () => {
    setBulkUsers([
      ...bulkUsers,
      {
        id: Date.now(),
        firstName: '',
        lastName: '',
        email: '',
        role: 'org_user',
        phone: '',
        department: '',
        position: '',
        password: '',
        reportingManagers: [],
        permissions: {
          addProject: false,
          addTeamMember: false,
          viewReports: true,
          viewActivity: true,
          editProfile: true,
        },
        expanded: false,
      },
    ]);
  };

  // Remove row from bulk users table
  const removeBulkUserRow = (id) => {
    if (bulkUsers.length > 1) {
      const newBulkUsers = bulkUsers.filter((user) => user.id !== id);
      setBulkUsers(newBulkUsers);
      
      // Reset pagination if current page becomes empty
      const totalPages = Math.ceil(newBulkUsers.length / bulkUsersPageSize);
      if (bulkUsersCurrentPage > totalPages && totalPages > 0) {
        setBulkUsersCurrentPage(totalPages);
      }
    } else {
      toast.error('At least one row is required');
    }
  };

  // Update a field in bulk user
  const updateBulkUser = (id, field, value) => {
    setBulkUsers(
      bulkUsers.map((user) =>
        user.id === id ? { ...user, [field]: value } : user
      )
    );
  };

  // Update reporting managers for a bulk user
  const updateBulkUserReportingManagers = (id, managerIds) => {
    setBulkUsers(
      bulkUsers.map((user) =>
        user.id === id ? { ...user, reportingManagers: managerIds } : user
      )
    );
  };

  // Update permissions for a bulk user
  const updateBulkUserPermission = (id, permission, value) => {
    setBulkUsers(
      bulkUsers.map((user) =>
        user.id === id
          ? {
              ...user,
              permissions: { ...user.permissions, [permission]: value },
            }
          : user
      )
    );
  };

  // Toggle expanded state for bulk user row
  const toggleBulkUserExpanded = (id) => {
    setBulkUsers(
      bulkUsers.map((user) =>
        user.id === id ? { ...user, expanded: !user.expanded } : user
      )
    );
  };

  // Apply to All Functions
  const applyReportingManagersToAll = () => {
    if (commonReportingManagers.length === 0) {
      toast.error('Please select at least one reporting manager first');
      return;
    }
    
    // Create a deep copy of the array to ensure proper state update
    const managersToApply = Array.isArray(commonReportingManagers) 
      ? [...commonReportingManagers] 
      : [];
    
    setBulkUsers(
      bulkUsers.map((user) => ({
        ...user,
        reportingManagers: managersToApply,
      }))
    );
    
    toast.success(`Applied ${managersToApply.length} reporting manager(s) to all ${bulkUsers.length} row(s)`);
  };

  const applyPermissionsToAll = () => {
    setBulkUsers(
      bulkUsers.map((user) => ({
        ...user,
        permissions: { ...commonPermissions },
      }))
    );
    
    toast.success(`Applied permissions to all ${bulkUsers.length} row(s)`);
  };

  const applyPositionToAll = () => {
    if (!commonPosition) {
      toast.error('Please select a position first');
      return;
    }
    
    setBulkUsers(
      bulkUsers.map((user) => ({
        ...user,
        position: commonPosition,
      }))
    );
    
    toast.success(`Applied position "${commonPosition}" to all ${bulkUsers.length} row(s)`);
  };

  const applyRoleToAll = (role) => {
    setBulkUsers(
      bulkUsers.map((user) => ({
        ...user,
        role: role,
        permissions: role === 'org_admin' ? undefined : user.permissions,
      }))
    );
    
    toast.success(`Applied role "${role}" to all ${bulkUsers.length} row(s)`);
  };

  const applyDepartmentToAll = (department) => {
    setBulkUsers(
      bulkUsers.map((user) => ({
        ...user,
        department: department || '',
      }))
    );

    toast.success(`Applied department "${department || '(empty)'}" to all ${bulkUsers.length} row(s)`);
  };

  const applyPasswordToAll = () => {
    const pwd = (commonPassword || '').trim();
    if (!pwd) {
      toast.error('Enter a common password first');
      return;
    }
    setBulkUsers(
      bulkUsers.map((user) => ({
        ...user,
        password: pwd,
      }))
    );
    toast.success(`Applied common password to all ${bulkUsers.length} row(s)`);
  };

  // CSV Upload Handler
  const handleCsvUpload = (file) => {
    setIsUploadingCsv(true);
    setCsvUploadError(null);
    setCsvUploadSuccess(false);

    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    // Check file type
    if (!['csv', 'xlsx', 'xls'].includes(fileExtension)) {
      setCsvUploadError('Invalid file type. Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
      setIsUploadingCsv(false);
      toast.error('Invalid file type. Please upload CSV or Excel file.');
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        let data = [];
        
        if (fileExtension === 'csv') {
          // Parse CSV
          const csvText = e.target.result;
          const parseResult = Papa.parse(csvText, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => header.trim(),
          });

          if (parseResult.errors.length > 0) {
            const errorMessages = parseResult.errors.map(err => 
              `Row ${err.row + 1}: ${err.message}`
            ).join(', ');
            throw new Error(`CSV parsing errors: ${errorMessages}`);
          }

          data = parseResult.data;
        } else {
          // Parse Excel
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          data = XLSX.utils.sheet_to_json(worksheet, {
            defval: '',
            raw: false,
          });
        }

        if (!data || data.length === 0) {
          throw new Error('File is empty or contains no data');
        }

        // Expected columns (case-insensitive matching)
        const expectedColumns = {
          required: ['First Name', 'Last Name', 'Email'],
          optional: ['Role', 'Position', 'Phone', 'Reporting Manager', 'Reporting Managers']
        };

        // Get actual columns from first row
        const actualColumns = Object.keys(data[0]).map(col => col.trim());
        
        // Normalize column names for matching (remove extra spaces, convert to lowercase)
        const normalizedActualColumns = actualColumns.map(col => 
          col.toLowerCase().replace(/\s+/g, ' ')
        );
        
        const normalizedExpectedRequired = expectedColumns.required.map(col => 
          col.toLowerCase()
        );

        // Check for required columns
        const missingColumns = [];
        normalizedExpectedRequired.forEach(expectedCol => {
          const found = normalizedActualColumns.some(actualCol => 
            actualCol === expectedCol || 
            actualCol.includes(expectedCol) || 
            expectedCol.includes(actualCol)
          );
          if (!found) {
            missingColumns.push(expectedColumns.required.find(col => 
              col.toLowerCase() === expectedCol
            ));
          }
        });

        if (missingColumns.length > 0) {
          throw new Error(
            `Missing required columns: ${missingColumns.join(', ')}. ` +
            `Found columns: ${actualColumns.join(', ')}. ` +
            `Please ensure your CSV has columns: ${expectedColumns.required.join(', ')}`
          );
        }

        // Map CSV data to bulkUsers format
        const mappedUsers = data.map((row, index) => {
          // Find matching column names (case-insensitive)
          const findColumn = (possibleNames) => {
            for (const actualCol of actualColumns) {
              const normalizedActual = actualCol.toLowerCase().trim();
              for (const possibleName of possibleNames) {
                const normalizedPossible = possibleName.toLowerCase().trim();
                if (normalizedActual === normalizedPossible || 
                    normalizedActual.includes(normalizedPossible) ||
                    normalizedPossible.includes(normalizedActual)) {
                  return actualCol;
                }
              }
            }
            return null;
          };

          const firstName = row[findColumn(['First Name', 'FirstName', 'first_name', 'First Name*'])] || '';
          const lastName = row[findColumn(['Last Name', 'LastName', 'last_name', 'Last Name*'])] || '';
          const email = row[findColumn(['Email', 'email', 'Email*', 'Email Address'])] || '';
          const role = row[findColumn(['Role', 'role', 'User Role'])] || 'org_user';
          const position = row[findColumn(['Position', 'position', 'Job Position'])] || '';
          const phone = row[findColumn(['Phone', 'phone', 'Phone Number', 'Mobile'])] || '';
          const password = row[findColumn(['Password', 'password', 'Pass', 'User Password'])] || '';
          
          // Handle reporting managers (can be comma-separated)
          const reportingManagerStr = row[findColumn(['Reporting Manager', 'Reporting Managers', 'Manager', 'reporting_manager'])] || '';
          const reportingManagers = reportingManagerStr 
            ? reportingManagerStr.split(',').map(m => m.trim()).filter(m => m)
            : [];

          return {
            id: Date.now() + index,
            firstName: firstName.toString().trim(),
            lastName: lastName.toString().trim(),
            email: email.toString().trim().toLowerCase(),
            role: resolveRoleInput(role.toString().trim(), roles).value,
            phone: phone.toString().trim(),
            department: '', // Department not imported from CSV, can be added manually
            position: position.toString().trim(),
            password: password.toString().trim(),
            reportingManagers: reportingManagers, // Keep as array of strings for now, will validate later
            permissions: {
              addProject: false,
              addTeamMember: false,
              viewReports: true,
              viewActivity: true,
              editProfile: true,
            },
            expanded: false,
          };
        }).filter(user => user.firstName || user.lastName || user.email); // Remove completely empty rows

        if (mappedUsers.length === 0) {
          throw new Error('No valid data found in the file. Please check your CSV format.');
        }

        // Validate basic required fields (First Name, Last Name, Email)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidRows = [];
        mappedUsers.forEach((user, index) => {
          const rowNumber = index + 2;
          if (!user.firstName || !user.lastName || !user.email) {
            invalidRows.push(`Row ${rowNumber}: Missing required fields (First Name, Last Name, or Email)`);
          } else if (!emailRegex.test(user.email)) {
            invalidRows.push(`Row ${rowNumber}: Invalid email format - ${user.email}`);
          }
        });

        if (invalidRows.length > 0) {
          throw new Error(
            `Validation errors found:\n${invalidRows.slice(0, 15).join('\n')}` +
            (invalidRows.length > 15 ? `\n... and ${invalidRows.length - 15} more errors` : '')
          );
        }

        // Validate Position and Reporting Manager data against team structure
        const structurePositionLabels = getAllStructurePositionNames(teamStructure);

        // Get available reporting managers
        const availableManagers = allUsers.filter(u => 
          u.role === 'org_admin' || u.position
        );

        // Create lookup maps for reporting managers
        const managerByEmail = {};
        const managerByName = {};

        availableManagers.forEach(manager => {
          if (manager.email) {
            managerByEmail[manager.email.toLowerCase().trim()] = manager._id;
          }
          const fullName = `${manager.firstName} ${manager.lastName}`.toLowerCase().trim();
          managerByName[fullName] = manager._id;
        });

        // Validate Position and Reporting Manager data.
        // Soft-validation: unknown Position / Reporting Manager values are dropped
        // (cleared) and the row still imports. Only hard-invalid data (bad Role)
        // blocks the upload. A summary toast warns about cleared fields so the
        // user can wire them up in the table or in Settings afterwards.
        const dataValidationErrors = [];
        const clearedPositions = [];   // [{ rowNumber, email, value }]
        const clearedManagers = [];    // [{ rowNumber, email, values: [] }]

        mappedUsers.forEach((user, index) => {
          const rowNumber = index + 2;

          // Validate Position — match against full team structure (not hierarchy-filtered dropdown)
          if (user.position && user.position.trim()) {
            const resolved = resolvePositionInput(user.position, teamStructure);
            if (resolved.matched) user.position = resolved.value;
            else {
              clearedPositions.push({
                rowNumber,
                email: user.email,
                value: user.position,
              });
              user.position = '';
            }
          }

          // Validate Reporting Managers — keep valid ones, drop unknowns
          if (user.reportingManagers && user.reportingManagers.length > 0) {
            const invalidManagers = [];
            const validManagerIds = [];

            user.reportingManagers.forEach((managerInput) => {
              const normalizedInput = managerInput.toLowerCase().trim();
              let foundManagerId = null;

              if (normalizedInput.includes('@')) {
                foundManagerId = managerByEmail[normalizedInput];
              } else {
                foundManagerId = managerByName[normalizedInput];

                if (!foundManagerId) {
                  const matchingManager = availableManagers.find(m => {
                    const fullName = `${m.firstName} ${m.lastName}`.toLowerCase().trim();
                    return fullName.includes(normalizedInput) || normalizedInput.includes(fullName);
                  });
                  if (matchingManager) {
                    foundManagerId = matchingManager._id;
                  }
                }
              }

              if (foundManagerId) {
                validManagerIds.push(foundManagerId);
              } else {
                invalidManagers.push(managerInput);
              }
            });

            // Always keep whatever resolved; drop the rest silently with a warning
            user.reportingManagers = validManagerIds;
            if (invalidManagers.length > 0) {
              clearedManagers.push({
                rowNumber,
                email: user.email,
                values: invalidManagers,
              });
            }
          }

          const resolvedRole = resolveRoleInput(user.role, roles);
          if (resolvedRole.matched) user.role = resolvedRole.value;
          else if (user.role) {
            dataValidationErrors.push(
              `Row ${rowNumber} (${user.email}): Invalid Role "${user.role}". Valid roles: ${roleImportHint(roles)}`
            );
          } else {
            user.role = 'org_user';
          }
        });

        // Surface a summary of dropped fields so the admin knows what to fix later
        if (clearedPositions.length > 0 || clearedManagers.length > 0) {
          const parts = [];
          if (clearedPositions.length > 0) {
            const sample = clearedPositions.slice(0, 3).map(c => `"${c.value}"`).join(', ');
            parts.push(
              `${clearedPositions.length} row(s) had an unknown Position (${sample}${clearedPositions.length > 3 ? '…' : ''}) — cleared. ` +
              `Available: ${structurePositionLabels.slice(0, 6).join(', ')}${structurePositionLabels.length > 6 ? '…' : ''}. Add positions in Settings → Team Structure.`
            );
          }
          if (clearedManagers.length > 0) {
            const sample = clearedManagers
              .slice(0, 3)
              .map(c => `"${c.values.join(', ')}"`)
              .join(', ');
            parts.push(
              `${clearedManagers.length} row(s) referenced a Reporting Manager that doesn't exist yet (${sample}${clearedManagers.length > 3 ? '…' : ''}) — cleared. Wire them up in the table or after import.`
            );
          }
          toast(parts.join('\n\n'), {
            icon: '⚠️',
            duration: 8000,
            style: { maxWidth: '520px', whiteSpace: 'pre-line' },
          });
          console.warn('[Bulk import] Cleared unknown fields:', { clearedPositions, clearedManagers });
        }

        // If data validation errors exist, show error and don't import
        if (dataValidationErrors.length > 0) {
          throw new Error(
            `Data validation errors found:\n${dataValidationErrors.slice(0, 20).join('\n')}` +
            (dataValidationErrors.length > 20 ? `\n... and ${dataValidationErrors.length - 20} more errors` : '') +
            `\n\nPlease fix these errors in your CSV and try again.`
          );
        }

        // All validations passed - Import the data
        setBulkUsers(mappedUsers);
        setBulkUsersCurrentPage(1); // Reset to first page after CSV import
        setCsvUploadSuccess(true);
        toast.success(`Successfully imported ${mappedUsers.length} user(s) from CSV!`);
        
      } catch (error) {
        console.error('CSV upload error:', error);
        setCsvUploadError(error.message);
        toast.error(`CSV Upload Error: ${error.message}`);
      } finally {
        setIsUploadingCsv(false);
      }
    };

    reader.onerror = () => {
      setCsvUploadError('Error reading file. Please try again.');
      setIsUploadingCsv(false);
      toast.error('Error reading file. Please try again.');
    };

    if (fileExtension === 'csv') {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  };

  // Bulk Add Handler
  const handleBulkAddUsers = async () => {
    setBulkUploadLoading(true);
    try {
      // Extract organization ID
      let organizationId;
      if (typeof user.organizationId === 'object') {
        organizationId = user.organizationId._id || user.organizationId.id;
      } else {
        organizationId = user.organizationId;
      }

      // Validate all users
      const validUsers = [];
      const errors = [];

      bulkUsers.forEach((user, index) => {
        if (!user.firstName || !user.lastName || !user.email) {
          errors.push({
            row: index + 1,
            email: user.email || 'Unknown',
            error: 'First Name, Last Name, and Email are required',
          });
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) {
          errors.push({
            row: index + 1,
            email: user.email,
            error: 'Invalid email format',
          });
        } else {
          // Handle reporting managers
          let reportingManager = '';
          let reportingManagersArray = [];
          if (user.reportingManagers && user.reportingManagers.length > 0) {
            reportingManager = user.reportingManagers[0];
            reportingManagersArray = user.reportingManagers;
          }

          const perRowPassword = user.password?.trim();
          validUsers.push({
            firstName: user.firstName.trim(),
            lastName: user.lastName.trim(),
            email: user.email.trim().toLowerCase(),
            role: user.role || 'org_user',
            phone: user.phone?.trim() || '',
            department: user.department?.trim() || '',
            position: user.position?.trim() || '',
            reportingManager: reportingManager,
            reportingManagers: reportingManagersArray,
            organizationId: organizationId,
            permissions: user.role === 'org_user' ? user.permissions : undefined,
            billingEnabled: true,
            ...(perRowPassword ? { password: perRowPassword } : {}),
          });
        }
      });

      if (validUsers.length === 0) {
        toast.error('Please add at least one valid user');
        setBulkUploadLoading(false);
        return;
      }

      // Check available slots before upload
      // Refresh organization data to get latest slots info
      await fetchOrganization();
      
      // Get updated organization data
      const updatedOrg = await api.get('/api/v1/billing/profile').then(res => res.data.data).catch(() => organization);
      
      // Skip slot checks if payment bypass is enabled (unlimited slots)
      if (!updatedOrg?.paymentBypassEnabled) {
        const isFreePlan = updatedOrg?.orgPlanSelection === 'free';
        const isPaidPlan = updatedOrg?.orgPlanSelection === 'standard' || updatedOrg?.orgPlanSelection === 'premium';
        const availableSlots = updatedOrg?.remainingSlots || 0;
        
        // Check slots for both paid and free plans
        if ((isPaidPlan || isFreePlan) && validUsers.length > availableSlots) {
          // Update organization state with latest data
          setOrganization(updatedOrg);
          
          toast.error(
            `Only ${availableSlots} slot${availableSlots !== 1 ? 's' : ''} available, but you are trying to upload ${validUsers.length} user${validUsers.length !== 1 ? 's' : ''}. Please reduce the number of users or purchase additional slots.`,
            { duration: 5000 }
          );
          setBulkUploadLoading(false);
          return;
        }
      }

      // Call bulk API
      const response = await api.post('/api/v1/users/bulk', {
        users: validUsers,
      });

      const { successCount, errorCount, errors: apiErrors } = response.data.data;

      if (successCount > 0) {
        toast.success(`Successfully added ${successCount} user(s)!`);
      }

      if (errorCount > 0 || errors.length > 0) {
        const allErrors = [...errors, ...(apiErrors || [])];
        toast.error(`${allErrors.length} user(s) failed. Check console for details.`);
        console.log('Failed users:', allErrors);
      }

      // Reset bulk form
      setBulkUsers([
        {
          id: 1,
          firstName: '',
          lastName: '',
          email: '',
          role: 'org_user',
          phone: '',
          department: '',
          position: '',
          password: '',
          reportingManagers: [],
          permissions: {
            addProject: false,
            addTeamMember: false,
            viewReports: true,
            viewActivity: true,
            editProfile: true,
          },
          expanded: false,
        },
      ]);

      // Reset common values
      setCommonReportingManagers([]);
      setCommonPassword('');
      setCommonPermissions({
        addProject: false,
        addTeamMember: false,
        viewReports: true,
        viewActivity: true,
        editProfile: true,
      });
      setCommonPosition('');
      setCommonRole('org_user');

      // Refresh data
      fetchUsers();
      await fetchOrganization();

      // Close modal
      setShowAddModal(false);
      setAddMode('single');
    } catch (error) {
      console.error('Bulk add error:', error);
      
      if (error.response) {
        // Build user-friendly error message
        let errorMessage = 'Failed to add users. Please check the form and try again.';
        
        // Check for validation error details (array format)
        if (error.response.data.error?.details && Array.isArray(error.response.data.error.details)) {
          const details = error.response.data.error.details;
          if (details.length > 0) {
            errorMessage = details.join('. ');
          }
        }
        // Check for validation errors (legacy format)
        else if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          errorMessage = 'Validation errors: ' + error.response.data.errors.join(', ');
        }
        // Check for error message
        else if (error.response.data.error?.message) {
          errorMessage = error.response.data.error.message;
        }
        // Check for top-level message
        else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        
        toast.error(errorMessage);
      } else {
        const errorMessage = error.message || 'Network error. Please try again.';
        toast.error(errorMessage);
      }
    } finally {
      setBulkUploadLoading(false);
    }
  };

  // ========== END BULK ADD FUNCTIONS ==========

  const handleEditUser = async (e) => {
    e.preventDefault();

    // Validate reporting manager is required when position is set
    if (formData.position && (!formData.reportingManagers || formData.reportingManagers.length === 0)) {
      setFormErrors(prev => ({ ...prev, reportingManagers: 'Reporting Manager is required' }));
      return;
    }
    setFormErrors(prev => ({ ...prev, reportingManagers: '' }));

    try {
      // Remove password from edit data if it's empty (don't update password)
      const editData = { ...formData };
      if (!editData.password || editData.password.trim() === '') {
        delete editData.password;
      }
      
      // Handle multiple reporting managers - send both formats for backward compatibility
      if (editData.reportingManagers && editData.reportingManagers.length > 0) {
        // If we have multiple reporting managers, use the first one as the primary reportingManager
        editData.reportingManager = editData.reportingManagers[0];
        // Keep the reportingManagers array for the new format
      } else if (editData.reportingManager && !editData.reportingManagers) {
        // If we only have a single reportingManager, convert it to array format
        editData.reportingManagers = [editData.reportingManager];
      }
      
      console.log('Updating user with reporting managers:', {
        userId: selectedUser._id,
        reportingManager: editData.reportingManager,
        reportingManagers: editData.reportingManagers
      });

      await api.put(`/api/v1/users/${selectedUser._id}`, editData);

      // console.log('User updated successfully with ID:', selectedUser._id);
      // console.log('User reporting managers updated:', editData.reportingManagers);

      // // Make additional API call to ensure reporting managers are properly updated
      // if (editData.reportingManagers) {
      //   try {
      //     await updateUserReportingManagers(selectedUser._id, editData.reportingManagers);
      //   } catch (reportingError) {
      //     console.warn('Primary API call succeeded but reporting managers update had an issue:', reportingError);
      //   }
      // }

      setShowEditModal(false);
      setSelectedUser(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        role: 'org_user',
        phone: '',
        department: '',
        position: '',
        reportingManager: '',
        reportingManagers: [],
        billingEnabled: true,
        password: '',
        invitationMethod: 'manual',
        sendEmailInvite: false,
        stealth_mode: false,
        permissions: {
          addProject: false,
          addTeamMember: false,
          viewReports: true,
          viewActivity: true,
          editProfile: true,
        },
      });
      setShowPassword(false);
      fetchUsers();
      toast.success('User updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
      console.error('Failed to update reporting managers for user:', selectedUser?._id);
      if (error.response) {
        console.error('Error response:', error.response.data);
        
        // Build user-friendly error message
        let errorMessage = 'Failed to update user. Please check the form and try again.';
        
        // Check for validation error details (array format)
        if (error.response.data.error?.details && Array.isArray(error.response.data.error.details)) {
          const details = error.response.data.error.details;
          if (details.length > 0) {
            errorMessage = details.join('. ');
          }
        }
        // Check for validation errors (legacy format)
        else if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          errorMessage = 'Validation errors: ' + error.response.data.errors.join(', ');
        }
        // Check for error message
        else if (error.response.data.error?.message) {
          errorMessage = error.response.data.error.message;
        }
        // Check for top-level message
        else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        
        toast.error(errorMessage);
      } else {
        const errorMessage = error.message || 'Network error. Please try again.';
        toast.error(errorMessage);
      }
    }
  };

  // Separate API call for updating reporting managers
  // const updateUserReportingManagers = async (userId, reportingManagers) => {
  //   try {
  //     console.log('Making separate API call to update reporting managers:', {
  //       userId,
  //       reportingManagers
  //     });

  //     const updateData = {
  //       reportingManagers: reportingManagers,
  //       // Set primary reporting manager as first in array for backward compatibility
  //       reportingManager: reportingManagers.length > 0 ? reportingManagers[0] : null
  //     };

  //     const response = await api.put(`/api/v1/users/${userId}`, updateData);

  //     console.log('Reporting managers updated successfully:', {
  //       userId,
  //       reportingManagers: response.data.data.reportingManagers
  //     });

  //     return response.data;
  //   } catch (error) {
  //     console.error('Error updating reporting managers:', error);
  //     if (error.response) {
  //       console.error('Error response:', error.response.data);
  //     }
  //     throw error;
  //   }
  // };

  const handleDeleteUser = (userId) => {
    const user = users.find(u => u.id === userId || u._id === userId);
    setUserToDelete({ id: userId, name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : 'this user' });
    setDeleteModalVisible(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setDeleteLoading(true);
      await api.delete(`/api/v1/users/${userToDelete.id}`);
      fetchUsers();
      toast.success('User deleted successfully');
      setDeleteModalVisible(false);
      setUserToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        if (error.response.data.error?.message) {
          toast.error(error.response.data.error.message);
        } else if (error.response.data.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error('Failed to delete user');
        }
      } else {
        const errorMessage = error.message || 'Network error. Please try again.';
        toast.error(errorMessage);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDeleteUser = () => {
    setDeleteModalVisible(false);
    setUserToDelete(null);
  };

  const handleArchiveUser = async (userId) => {
    if (window.confirm('Are you sure you want to archive this user?')) {
      try {
        await api.put(`/api/v1/users/${userId}`, { status: 'archived' });
        fetchUsers();
        toast.success('User archived successfully');
      } catch (error) {
        console.error('Error archiving user:', error);
        toast.error('Failed to archive user');
      }
    }
  };

  const handleToggleBilling = async (userItem) => {
    const newBillingStatus = !userItem.billingEnabled;
    const action = newBillingStatus ? 'enable' : 'disable';
    
    if (window.confirm(`Are you sure you want to ${action} billing for ${userItem.firstName} ${userItem.lastName}?`)) {
      try {
        // Set loading state for this specific user
        setBillingUpdateLoading(prev => ({ ...prev, [userItem.id]: true }));
        
        await api.put(`/api/v1/users/${userItem.id}`, { 
          billingEnabled: newBillingStatus 
        });
        
        // Update local state immediately for better UX
        setUsers(prevUsers => 
          prevUsers.map(u => 
            u.id === userItem.id 
              ? { ...u, billingEnabled: newBillingStatus }
              : u
          )
        );
        
        // Refresh users to update the billing count
        fetchUsers();
        
        toast.success(`Billing ${action}d successfully for ${userItem.firstName}`);
      } catch (error) {
        console.error('Error updating billing status:', error);
        toast.error(`Failed to ${action} billing for ${userItem.firstName}`);
      } finally {
        // Clear loading state
        setBillingUpdateLoading(prev => ({ ...prev, [userItem.id]: false }));
      }
    }
  };



  const copyInvitationLink = async () => {
    try {
      await navigator.clipboard.writeText(invitationLink);
      toast.success('Invitation link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleStealthModeToggle = (userId, newValue) => {
    const user = users.find(u => u.id === userId || u._id === userId);
    setUserToToggleStealth({ 
      id: userId, 
      name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : 'this user',
      newValue: newValue
    });
    setStealthModeModalVisible(true);
  };

  const confirmStealthModeToggle = async () => {
    if (!userToToggleStealth) return;
    
    try {
      setStealthModeLoading(true);
      const response = await api.put(`/api/v1/users/${userToToggleStealth.id}`, {
        stealth_mode: userToToggleStealth.newValue,
      });

      if (response.data.success) {
        // Update local state
        setUsers(users.map(u => 
          u.id === userToToggleStealth.id || u._id === userToToggleStealth.id 
            ? { ...u, stealth_mode: userToToggleStealth.newValue }
            : u
        ));
        toast.success(`Background mode ${userToToggleStealth.newValue ? 'enabled' : 'disabled'} successfully!`);
        setStealthModeModalVisible(false);
        setUserToToggleStealth(null);
      }
    } catch (error) {
      console.error('Error toggling stealth mode:', error);
      if (error.response?.data?.error?.message) {
        toast.error(error.response.data.error.message);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update background mode');
      }
    } finally {
      setStealthModeLoading(false);
    }
  };

  const cancelStealthModeToggle = () => {
    setStealthModeModalVisible(false);
    setUserToToggleStealth(null);
  };

  // Bulk Stealth Mode Handlers
  const handleBulkStealthModeToggle = (newValue) => {
    setBulkStealthModeValue(newValue);
    setBulkStealthModeModalVisible(true);
  };

  const confirmBulkStealthModeToggle = async () => {
    if (bulkStealthModeValue === null) return;
    
    try {
      setBulkStealthModeLoading(true);
      const response = await api.put('/api/v1/users/bulk-stealth-mode', {
        stealth_mode: bulkStealthModeValue,
      });

      if (response.data.success) {
        // Update all users in local state
        setUsers(users.map(u => ({
          ...u,
          stealth_mode: bulkStealthModeValue
        })));
        
        // Also update allUsers if it exists
        if (allUsers && allUsers.length > 0) {
          setAllUsers(allUsers.map(u => ({
            ...u,
            stealth_mode: bulkStealthModeValue
          })));
        }
        
        toast.success(`Bulk background mode ${bulkStealthModeValue ? 'enabled' : 'disabled'} for ${response.data.data.updatedCount} members!`);
        setBulkStealthModeModalVisible(false);
        setBulkStealthModeValue(null);
      }
    } catch (error) {
      console.error('Error toggling bulk stealth mode:', error);
      if (error.response?.data?.error?.message) {
        toast.error(error.response.data.error.message);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update bulk background mode');
      }
    } finally {
      setBulkStealthModeLoading(false);
    }
  };

  const cancelBulkStealthModeToggle = () => {
    setBulkStealthModeModalVisible(false);
    setBulkStealthModeValue(null);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    
    // Handle multiple reporting managers - check if user has reportingManagers array or single reportingManager
    let reportingManagers = [];
    if (user.reportingManagers && Array.isArray(user.reportingManagers)) {
      // Normalize: extract IDs from objects or use IDs directly
      reportingManagers = user.reportingManagers.map(manager => {
        // If manager is an object with _id, extract the ID
        if (typeof manager === 'object' && manager !== null && manager._id) {
          return manager._id;
        }
        // Otherwise, it's already an ID
        return manager;
      }).filter(Boolean); // Remove any null/undefined values
    } else if (user.reportingManager) {
      // Old format: single manager - convert to array
      const managerId = typeof user.reportingManager === 'object' ? user.reportingManager._id : user.reportingManager;
      if (managerId) {
        reportingManagers = [managerId];
      }
    }
    
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      phone: user.phone || '',
      department: user.department || '',
      position: user.position || '',
      reportingManager: typeof user.reportingManager === 'object' ? user.reportingManager._id : (user.reportingManager || ''),
      reportingManagers: reportingManagers,
      billingEnabled: user.billingEnabled !== undefined ? user.billingEnabled : true,
      stealth_mode: user.stealth_mode || false,
      // Edit-user form: spread the existing user's permissions, falling back
      // to defaults when the field is missing. crmAccess in particular may be
      // absent on old user docs — the checkbox uses `!!formData.permissions.crmAccess`
      // so undefined renders as unchecked. We don't write crmAccess back into
      // the form here because the checkbox handler sets it on change.
      permissions: user.permissions || {
        addProject: false,
        addTeamMember: false,
        viewReports: true,
        viewActivity: true,
        editProfile: true,
        crmAccess: false,
      },
    });
    setShowEditModal(true);
  };

  // Use users directly since filtering is now handled server-side
  const displayUsers = users;

  const getRoleInfo = (role) => {
    return roles.find(r => r.value === role) || roles[0];
  };

  const getPositionInfo = (position) => {
    const pos = positions.find(p => p.value === position);
    if (pos) {
      return pos;
    }
    
    // Fallback for positions that might not be in team structure
    return {
      value: position || 'N/A',
      label: position || 'N/A',
      color: 'bg-gray-500/20 text-gray-400'
    };
  };

  // Get position level from team structure
  const getPositionLevel = (position) => {
    if (!position || !teamStructure || teamStructure.length === 0) {
      return -1;
    }

    for (let i = 0; i < teamStructure.length; i++) {
      const level = teamStructure[i];
      const pos = level.positions.find(p => p.roleName === position);
      if (pos) {
        return i + 1; // Return 1-based level number
      }
    }
    return -1; // Position not found in team structure
  };

  // Get help text for reporting manager selection
  const getReportingManagerHelpText = (position) => {
    const currentLevel = getPositionLevel(position);
    if (currentLevel > 0) {
      const eligibleLevels = [];
      for (let i = 1; i < currentLevel; i++) {
        eligibleLevels.push(`Level ${i}`);
      }
      return `Your position is Level ${currentLevel}. You can select managers from: ${eligibleLevels.join(', ')}`;
    }
    return 'Select a manager from a higher level in the organizational hierarchy';
  };

  // Toggle reporting manager selection
  const toggleReportingManager = (managerId) => {
    setFormData(prev => {
      const currentManagers = prev.reportingManagers || [];
      const isSelected = currentManagers.includes(managerId);
      
      if (isSelected) {
        // Remove manager
        return {
          ...prev,
          reportingManagers: currentManagers.filter(id => id !== managerId)
        };
      } else {
        // Add manager
        return {
          ...prev,
          reportingManagers: [...currentManagers, managerId]
        };
      }
    });
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.reporting-managers-dropdown')) {
        setIsReportingManagersDropdownOpen(false);
        setIsEditReportingManagersDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Helper function to check if user can add team members
  const canAddTeamMembers = () => {
    // Main admin and org admin can always add members
    if (user?.role === 'main_admin' || user?.role === 'org_admin') return true;
    
    // For org_user, check if they have addTeamMember permission
    if (user?.role === 'org_user' && user?.permissions?.addTeamMember) {
      return true;
    }
    
    return false;
  };

  // Helper function to get options for reporting managers select, including already-selected managers
  const getReportingManagersOptions = (selectedPosition, currentUserId = null, selectedManagerIds = []) => {
    // Get available managers based on position
    const availableManagers = getAvailableReportingManagers(selectedPosition, currentUserId);
    const availableManagerIds = new Set(availableManagers.map(m => String(m._id)));
    
    // Find selected managers that are not in available list (from allUsers)
    const selectedIds = selectedManagerIds.map(id => String(id));
    const missingManagers = selectedIds
      .filter(id => !availableManagerIds.has(id))
      .map(id => {
        // Find the manager in allUsers
        const manager = allUsers.find(u => String(u._id) === id);
        return manager;
      })
      .filter(Boolean); // Remove any undefined values
    
    // Combine available and missing managers, remove duplicates
    const allOptions = [...availableManagers, ...missingManagers];
    const uniqueManagers = Array.from(
      new Map(allOptions.map(m => [String(m._id), m])).values()
    );
    
    return uniqueManagers.map(manager => ({
      value: manager._id,
      label: `${manager.firstName} ${manager.lastName} (${manager.position || manager.role})`
    }));
  };

  // Get available reporting managers based on team structure hierarchy
  const getAvailableReportingManagers = (selectedPosition, currentUserId = null) => {
    console.log('🔍 DEBUG - getAvailableReportingManagers called:', {
      selectedPosition,
      currentUserId,
      userRole: user.role,
      userPosition: user.position,
      teamStructureLength: teamStructure.length,
      usersLength: users.length,
      allUsersLength: allUsers.length
    });

    // For org_user role, show all users who can be reporting managers
    if (user.role === 'org_user') {
      // Get current user's level
      let currentUserLevel = -1;
      for (let i = 0; i < teamStructure.length; i++) {
        const level = teamStructure[i];
        console.log('🔍 DEBUG - Checking level', i, ':', level.levelName, 'positions:', level.positions?.map(p => p.roleName));
        if (level.positions && level.positions.some(pos => pos.roleName.trim() === user.position.trim())) {
          currentUserLevel = i;
          console.log('🔍 DEBUG - Found user position at level:', i);
          break;
        }
      }

      console.log('🔍 DEBUG - Current user level:', currentUserLevel);

      // If user's position not found, return empty array
      if (currentUserLevel === -1) {
        console.log('🔍 DEBUG - User position not found, returning empty array');
        return [];
      }

      // Get positions at lower levels only (can be reporting managers)
      // Exclude same level users, only include current user and lower levels
      const eligiblePositions = [];
      for (let i = currentUserLevel + 1; i < teamStructure.length; i++) { // Start from level below current user
        const level = teamStructure[i];
        if (level.positions) {
          level.positions.forEach(position => {
            eligiblePositions.push(position.roleName);
          });
        }
      }
      
      // Add current user's position to eligible positions (they can report to themselves)
      if (user.position) {
        eligiblePositions.push(user.position);
      }

      console.log('🔍 DEBUG - Eligible positions:', eligiblePositions);
      console.log('🔍 DEBUG - Eligible positions (trimmed):', eligiblePositions.map(pos => `"${pos.trim()}"`));
      console.log('🔍 DEBUG - All users in allUsers:', allUsers.map(u => `${u.firstName} ${u.lastName} - Position: "${u.position}" - Role: ${u.role}`));

      // Filter users who can be reporting managers (use allUsers for complete list)
      const eligibleUsers = allUsers.filter(u => {
        // Don't allow self-selection for edit mode
        if (currentUserId && u._id === currentUserId) return false;
        
        // For current user's position, only allow the current user themselves
        if (u.position && u.position.trim() === user.position.trim()) {
          const isCurrentUser = u._id === user.id;
          console.log('🔍 DEBUG - User', u.firstName, u.lastName, 'eligible:', isCurrentUser, 'role:', u.role, 'position:', u.position, '(same position as current user)');
          return isCurrentUser;
        }
        
        // For lower level positions, allow all users with those positions
        const isLowerLevel = u.position && eligiblePositions.some(pos => pos.trim() === u.position.trim());
        console.log('🔍 DEBUG - User', u.firstName, u.lastName, 'eligible:', isLowerLevel, 'role:', u.role, 'position:', u.position);
        return isLowerLevel;
      });

      console.log('🔍 DEBUG - Final eligible users:', eligibleUsers.length);
      return eligibleUsers;
    }
    
    if (!selectedPosition || !teamStructure || teamStructure.length === 0) {
      return allUsers.filter(user => {
        // Don't allow self-selection
        if (currentUserId && user._id === currentUserId) return false;
        return user.role === 'org_admin' || user.position;
      });
    }

    // Find the level of the selected position
    let selectedLevel = -1;

    for (let i = 0; i < teamStructure.length; i++) {
      const level = teamStructure[i];
      const position = level.positions.find(p => p.roleName.trim() === selectedPosition.trim());
      if (position) {
        selectedLevel = i;
        break;
      }
    }

    // If position not found in team structure, allow org admins only
    if (selectedLevel === -1) {
      return allUsers.filter(user => {
        // Don't allow self-selection
        if (currentUserId && user._id === currentUserId) return false;
        return user.role === 'org_admin';
      });
    }

    // Get users who can be reporting managers (only from higher levels)
    const availableManagers = allUsers.filter(user => {
      // Don't allow self-selection
      if (currentUserId && user._id === currentUserId) return false;

      // Organization admins can always be reporting managers
      if (user.role === 'org_admin') return true;

      // If user has a position, check if it's at the same level or higher level
      if (user.position) {
        for (let i = 0; i <= selectedLevel; i++) {
          const level = teamStructure[i];
          if (level.positions.find(p => p.roleName.trim() === user.position.trim())) {
            return true;
          }
        }
      }

      return false;
    });

    return availableManagers;
  };

  // Restrict visible members for org_user to self + lower levels in teamStructure
  const canViewMember = (currentUser, member) => {
    if (!currentUser || !member) return false;
    if (currentUser.role === 'main_admin' || currentUser.role === 'org_admin') return true;
    if (currentUser.role !== 'org_user') return true;

    const normalize = (s) => (s || '').trim().toLowerCase();

    const currentLevel = teamStructure.findIndex(
      (lvl) => lvl.positions?.some((p) => normalize(p.roleName) === normalize(currentUser.position))
    );
    const memberLevel = teamStructure.findIndex(
      (lvl) => lvl.positions?.some((p) => normalize(p.roleName) === normalize(member.position))
    );

    // If level cannot be resolved, only allow viewing self
    if (currentLevel === -1 || memberLevel === -1) {
      return (member._id?.toString && member._id?.toString() === currentUser.id) || (member.id === currentUser.id);
    }

    // org_user: can view self and lower levels (greater index)
    const isSelf = (member._id?.toString && member._id?.toString() === currentUser.id) || (member.id === currentUser.id);
    return isSelf || memberLevel > currentLevel;
  };

  // Calculate statistics for count cards
  const calculateStatistics = () => {
    // Count roles from the dynamic roles array (from org settings)
    const totalRoles = roles.length;
    
    // Count positions from team structure (not from assigned users)
    const totalPositions = teamStructure.reduce((total, level) => {
      return total + (level.positions ? level.positions.length : 0);
    }, 0);
    
    // Count levels from team structure
    const totalLevels = teamStructure.length;
    
    // Count users with billing enabled from the complete list of all users
    const billingEnabledCount = allUsers.filter(u => u.billingEnabled).length;
    
    return {
      total: totalUsers,
      roles: totalRoles,
      positions: totalPositions, // Now shows 16 from team structure
      levels: totalLevels, // Shows 5 levels
      billingEnabled: billingEnabledCount
    };
  };

  const stats = calculateStatistics();

  // Calculate bulk stealth mode state (checked if all users have stealth_mode enabled)
  const bulkStealthModeChecked = useMemo(() => {
    if (!users || users.length === 0) return false;
    // Check if all users have stealth_mode enabled
    return users.every(u => u.stealth_mode === true);
  }, [users]);

  // Open Add Panel
  const openAddPanel = async () => {
    // Refresh organization data to get latest slots info
    await fetchOrganization();
    
    // Check slots BEFORE opening modal (for both paid and free plans)
    const isFreePlan = organization?.orgPlanSelection === 'free';
    const isPaidPlan = organization?.orgPlanSelection === 'standard' || organization?.orgPlanSelection === 'premium';
    
    // Get updated organization data after fetch
    const updatedOrg = await api.get('/api/v1/billing/profile').then(res => res.data.data).catch(() => organization);
    
    // Skip slot checks if payment bypass is enabled (unlimited slots)
    if (updatedOrg?.paymentBypassEnabled) {
      console.log('✅ [TEAM] Payment bypass enabled - unlimited slots, skipping slot checks');
    } else {
      // Check slots for both paid and free plans
      if ((isPaidPlan || isFreePlan) && (updatedOrg?.remainingSlots || 0) <= 0) {
        // Update organization state with latest data
        setOrganization(updatedOrg);
        
        if (isFreePlan) {
          // Free plan: Show upgrade modal first (not slot purchase)
          toast.error('You have used all 5 free slots. Please upgrade your plan to add more members.');
          setShowUpgradeModal(true);
          return; // Don't open add modal
        } else {
          // Paid plan: Show slot purchase modal directly
          toast.error('All slots are exhausted. Please purchase additional slots to add a team member.');
          setPendingUser({
            id: null, // Will be set after user creation
            name: 'New Team Member',
            email: '',
            needsAdditionalSlots: true
          });
          setAdditionalSlotsQuantity(1); // Reset to 1 slot
          setShowPaymentModal(true);
          return; // Don't open add modal
        }
      }
    }
    
    // Reset form data to empty state for add
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      role: 'org_user',
      phone: '',
      department: '',
      position: '',
      reportingManager: '',
      billingEnabled: true,
      password: '',
      invitationMethod: 'manual',
      sendEmailInvite: false,
      permissions: {
        addProject: false,
        addTeamMember: false,
        viewReports: true,
        viewActivity: true,
        editProfile: true,
      },
    });
    setShowPassword(false);
    // Clear form errors when opening
    setFormErrors({
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      position: '',
      password: '',
      reportingManagers: '',
    });
    setShowAddModal(true);
    setTimeout(() => setIsAddPanelAnimating(true), 10);
  };

  // Close Add Panel
  const closeAddPanel = () => {
    setIsAddPanelAnimating(false);
    setTimeout(() => {
      setShowAddModal(false);
      setShowPassword(false);
      setAddMode('single'); // Reset to single mode
      
      // Reset CSV upload states
      setCsvFile(null);
      setCsvUploadError(null);
      setCsvUploadSuccess(false);
      setIsUploadingCsv(false);
      
      // Reset single form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        role: 'org_user',
        phone: '',
        department: '',
        position: '',
        reportingManager: '',
        reportingManagers: [],
        billingEnabled: true,
        password: '',
        invitationMethod: 'manual',
        sendEmailInvite: false,
        permissions: {
          addProject: false,
          addTeamMember: false,
          viewReports: true,
          viewActivity: true,
          editProfile: true,
        },
      });
      // Clear form errors when closing
      setFormErrors({
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        position: '',
        password: '',
        reportingManagers: '',
      });
      
      // Reset bulk form
      setBulkUsers([
        {
          id: 1,
          firstName: '',
          lastName: '',
          email: '',
          role: 'org_user',
          phone: '',
          department: '',
          position: '',
          password: '',
          reportingManagers: [],
          permissions: {
            addProject: false,
            addTeamMember: false,
            viewReports: true,
            viewActivity: true,
            editProfile: true,
          },
          expanded: false,
        },
      ]);

      // Reset common values
      setCommonReportingManagers([]);
      setCommonPermissions({
        addProject: false,
        addTeamMember: false,
        viewReports: true,
        viewActivity: true,
        editProfile: true,
      });
      setCommonPosition('');
      setCommonRole('org_user');
      setCommonPassword('');

      // Reset bulk users pagination
      setBulkUsersCurrentPage(1);
      setBulkUsersPageSize(10);
    }, 300);
  };

  // Open Edit Panel
  const openEditPanel = (userItem) => {
    setSelectedUser(userItem);
    
    // Extract reporting manager ID properly (handle both string and object formats)
    let reportingManagerId = '';
    if (userItem.reportingManager) {
      if (typeof userItem.reportingManager === 'object') {
        // Handle populated object from backend
        reportingManagerId = userItem.reportingManager._id || userItem.reportingManager.id || '';
      } else {
        // Handle string ID
        reportingManagerId = userItem.reportingManager;
      }
    }

    // Handle multiple reporting managers - check if user has reportingManagers array or single reportingManager
    let reportingManagers = [];
    if (userItem.reportingManagers && Array.isArray(userItem.reportingManagers)) {
      // Normalize: extract IDs from objects or use IDs directly
      reportingManagers = userItem.reportingManagers.map(manager => {
        // If manager is an object with _id, extract the ID
        if (typeof manager === 'object' && manager !== null && manager._id) {
          return manager._id;
        }
        // Otherwise, it's already an ID
        return manager;
      }).filter(Boolean); // Remove any null/undefined values
    } else if (userItem.reportingManager) {
      // Old format: single manager - convert to array
      const managerId = typeof userItem.reportingManager === 'object' ? userItem.reportingManager._id : userItem.reportingManager;
      if (managerId) {
        reportingManagers = [managerId];
      }
    }

    // Debug logging
    console.log('Edit user data:', {
      user: userItem.firstName + ' ' + userItem.lastName,
      reportingManager: userItem.reportingManager,
      reportingManagers: reportingManagers,
      rawReportingManagers: userItem.reportingManagers,
      reportingManagerId: reportingManagerId,
      position: userItem.position
    });

    setFormData({
      firstName: userItem.firstName,
      lastName: userItem.lastName,
      email: userItem.email,
      role: userItem.role,
      phone: userItem.phone || '',
      department: userItem.department || '',
      position: userItem.position || '',
      reportingManager: reportingManagerId,
      reportingManagers: reportingManagers,
      billingEnabled: userItem.billingEnabled || false,
      password: '',
      invitationMethod: 'manual',
      sendEmailInvite: false,
      permissions: userItem.permissions || {
        addProject: false,
        addTeamMember: false,
        viewReports: true,
        viewActivity: true,
        editProfile: true,
      },
    });
    setShowPassword(false);
    setShowEditModal(true);
    setTimeout(() => setIsEditPanelAnimating(true), 10);
  };

  // Close Edit Panel
  const closeEditPanel = () => {
    setIsEditPanelAnimating(false);
    setTimeout(() => {
      setShowEditModal(false);
      setSelectedUser(null);
      setShowPassword(false);
    }, 300);
  };

  // Ant Design ConfigProvider theme
  const antConfigTheme = {
    algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    token: {
      colorPrimary: '#6C63FF',
      colorBgContainer: isDark ? '#13131A' : '#FFFFFF',
      colorBgElevated: isDark ? '#1A1A24' : '#F8F8FC',
      borderRadius: 12,
      colorBorder: isDark ? '#26263A' : '#E4E4EE',
      colorText: isDark ? '#EEEEF5' : '#0E0E1A',
      colorTextSecondary: '#7B7B9D',
    }
  };

  // Helper: resolve reporting managers for a userItem
  const resolveReportingManagers = (userItem) => {
    const mgrs = [];
    if (userItem.reportingManagers && Array.isArray(userItem.reportingManagers) && userItem.reportingManagers.length > 0) {
      userItem.reportingManagers.forEach(manager => {
        if (typeof manager === 'object' && manager._id) {
          mgrs.push(manager);
        } else if (typeof manager === 'string') {
          const foundManager = users.find(u => u._id.toString() === manager.toString());
          if (foundManager) mgrs.push(foundManager);
        }
      });
    } else if (userItem.reportingManager) {
      if (typeof userItem.reportingManager === 'object' && userItem.reportingManager._id) {
        mgrs.push(userItem.reportingManager);
      } else if (typeof userItem.reportingManager === 'string') {
        const manager = users.find(u => u._id.toString() === userItem.reportingManager.toString());
        if (manager) mgrs.push(manager);
      }
    }
    return mgrs;
  };

  return (
    <PageAnimationWrapper>
      <ConfigProvider theme={antConfigTheme}>
      <div className={`team-page ${isDark ? '' : 'light'}`}>
      <AnimatePresence mode="wait">
        {loading && users.length === 0 ? (
          <motion.div
            key="skeleton"
            variants={fadeInVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.2 }}
            style={{ padding: '24px' }}
          >
          {/* Header Skeleton */}
          <div style={{ marginBottom: 24 }}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Skeleton.Input active size="large" style={{ width: 220, height: 36, borderRadius: 10 }} />
                </div>
                <Skeleton.Input active size="small" style={{ width: 400, height: 16, borderRadius: 6 }} />
              </div>
              <div className="hidden lg:flex items-center gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '14px 20px', textAlign: 'center' }}>
                    <Skeleton.Input active style={{ width: 36, height: 28, borderRadius: 6 }} />
                    <Skeleton.Input active style={{ width: 60, height: 12, borderRadius: 4, marginTop: 4 }} />
                  </div>
                ))}
              </div>
            </div>
            <div className="shimmer-separator" />
          </div>

          {/* Controls Skeleton */}
          <div className="flex items-center gap-3" style={{ marginBottom: 24 }}>
            <Skeleton.Input active size="large" style={{ flex: 1, height: 42, borderRadius: 12 }} />
            <Skeleton.Button active style={{ width: 180, height: 42, borderRadius: 12 }} />
            <Skeleton.Button active style={{ width: 140, height: 42, borderRadius: 12 }} />
            <Skeleton.Button active style={{ width: 130, height: 42, borderRadius: 999 }} />
          </div>

          {/* Cards Grid Skeleton */}
          <div className="cards-grid">
            {Array.from({ length: 9 }).map((_, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="member-card"
              >
                {/* Accent bar */}
                <div className="card-accent" />

                {/* Header: Avatar + Name/Position + Action icons */}
                <div className="card-header">
                  <Skeleton.Avatar active size={38} shape="circle" />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Skeleton.Input active style={{ width: 120, height: 16, borderRadius: 4 }} />
                    <Skeleton.Input active style={{ width: 80, height: 12, borderRadius: 4 }} />
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Skeleton.Avatar active size={26} shape="square" style={{ borderRadius: 6 }} />
                    <Skeleton.Avatar active size={26} shape="square" style={{ borderRadius: 6 }} />
                  </div>
                </div>

                {/* Badges row */}
                <div className="card-badges">
                  <Skeleton.Button active size="small" style={{ width: 60, height: 22, borderRadius: 999 }} />
                  <Skeleton.Button active size="small" style={{ width: 70, height: 22, borderRadius: 999 }} />
                  <Skeleton.Button active size="small" style={{ width: 50, height: 22, borderRadius: 999 }} />
                </div>

                {/* Stats row */}
                <div className="card-stats-row">
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Skeleton.Avatar active size={14} shape="circle" />
                    <Skeleton.Input active style={{ width: 16, height: 14, borderRadius: 3 }} />
                    <Skeleton.Input active style={{ width: 50, height: 12, borderRadius: 3 }} />
                  </div>
                  <span className="stat-divider" />
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Skeleton.Avatar active size={14} shape="circle" />
                    <Skeleton.Input active style={{ width: 16, height: 14, borderRadius: 3 }} />
                    <Skeleton.Input active style={{ width: 40, height: 12, borderRadius: 3 }} />
                  </div>
                  <span className="stat-divider" />
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Skeleton.Input active style={{ width: 52, height: 12, borderRadius: 3 }} />
                    <Skeleton.Avatar active size={22} shape="circle" />
                    <Skeleton.Avatar active size={22} shape="circle" style={{ marginLeft: -8 }} />
                  </div>
                </div>

                {/* Toggle row */}
                <div className="card-toggle-row">
                  <Skeleton.Input active style={{ width: 110, height: 13, borderRadius: 4 }} />
                  <Skeleton.Button active style={{ width: 36, height: 18, borderRadius: 999 }} />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
        ) : (
          <motion.div
            key="content"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={pageTransition}
            style={{ padding: '24px' }}
          >
        {/* Header */}
        <TeamHeader stats={stats} user={user} isDark={isDark} />

        {/* Controls */}
        <TeamControls
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          roleFilter={roleFilter}
          onRoleFilterChange={handleRoleFilterChange}
          roles={roles}
          isDark={isDark}
          user={user}
          bulkStealthModeChecked={bulkStealthModeChecked}
          onBulkStealthModeToggle={handleBulkStealthModeToggle}
          bulkStealthModeLoading={bulkStealthModeLoading}
          canAddTeamMembers={canAddTeamMembers()}
          onAddMember={openAddPanel}
          onTeamStructure={() => navigate('/dashboard/settings?section=team')}
        />

        {/* Main Content */}
        <div>
          {displayUsers.length === 0 ? (
            <div className="empty-state">
              <Users size={56} className="empty-state-icon" style={{ margin: '0 auto 16px' }} />
              <h3 className="empty-state-title">No Team Members Found</h3>
              <p className="empty-state-text">
                {searchTerm || roleFilter
                  ? 'No team members match your search criteria. Try adjusting your filters.'
                  : 'Get started by adding your first team member to the organization.'}
              </p>
              {canAddTeamMembers() && (
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="btn-primary"
                  onClick={openAddPanel}
                  style={{ margin: '0 auto' }}
                >
                  <UserPlus size={16} />
                  Add Your First Team Member
                </motion.button>
              )}
            </div>
          ) : (
            <div className="cards-grid">
              {(user?.role === 'org_user' ? displayUsers.filter((u) => canViewMember(user, u)) : displayUsers).map((userItem, index) => {
                const roleInfo = getRoleInfo(userItem.role);
                const positionInfo = getPositionInfo(userItem.position);
                const reportingManagers = resolveReportingManagers(userItem);

                return (
                  <TeamMemberCard
                    key={userItem.id}
                    userItem={userItem}
                    index={index}
                    isDark={isDark}
                    currentUser={user}
                    reportingManagers={reportingManagers}
                    roleInfo={roleInfo}
                    positionInfo={positionInfo}
                    onEdit={openEditPanel}
                    onDelete={handleDeleteUser}
                    onPermissionsClick={handlePermissionsClick}
                    getPermissionsCount={getPermissionsCount}
                    onStealthModeToggle={handleStealthModeToggle}
                    onPayment={(item) => {
                      setPendingUser({
                        id: item.id,
                        name: `${item.firstName} ${item.lastName}`,
                        email: item.email,
                        needsAdditionalSlots: true
                      });
                      setAdditionalSlotsQuantity(1);
                      setShowPaymentModal(true);
                    }}
                    onProjectsClick={(item) => navigate(`/dashboard/projects?userId=${item.id || item._id}`)}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Permissions Tooltip */}
      {permissionsTooltip.show && (
        <>
        <div
          className="fixed inset-0 z-40"
          onClick={closePermissionsTooltip}
        />
        <div className="permissions-tooltip" style={{ left: permissionsTooltip.x, top: permissionsTooltip.y }}>
          <div className="permissions-tooltip-title">Permissions:</div>
          <div>
            {getPermissionsList(displayUsers.find(u => u._id === permissionsTooltip.userId)).map((permission, index) => (
              <div key={index} className="permissions-tooltip-item">
                • {permission}
              </div>
            ))}
          </div>
          <button
            onClick={closePermissionsTooltip}
            style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)' }}
          >
            <X size={14} />
          </button>
        </div>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-bar">
          <div className="flex items-center gap-3">
            <span className="pagination-info">Show:</span>
            <div style={{ width: 80 }}>
              <AntDropdown
                value={pageSize}
                onChange={(value) => handlePageSizeChange(value)}
                options={[
                  { value: 10, label: '10' },
                  { value: 20, label: '20' },
                  { value: 50, label: '50' }
                ]}
                placeholder="20"
                showSearch={false}
                allowClear={false}
              />
            </div>
            <span className="pagination-info">per page</span>
          </div>

          <span className="pagination-info">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers}
          </span>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>
      )}

        {/* Add User Panel */}
        {showAddModal && (
          <>
            {/* Backdrop */}
            <div
              className={`panel-backdrop ${isDark ? 'dark' : 'light'}`}
              onClick={closeAddPanel}
            />

            {/* Slide-in Panel */}
            <div className={`slide-panel ${isAddPanelAnimating ? 'open' : ''}`} style={{
              width: addMode === 'bulk' ? 'min(1200px, 90vw)' : 'min(600px, 100vw)',
            }}>
              <div className="panel-container">
              {/* Header */}
              <div className="panel-header">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isDark ? 'bg-white/15' : 'bg-indigo-100'
                    }`}>
                      <UserPlus className={`w-5 h-5 ${isDark ? 'text-white' : 'text-indigo-600'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {addMode === 'single' ? 'Add Team Member' : 'Add Team Members (Bulk)'}
                          </h3>
                          <p className={`text-sm mt-1 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                            {addMode === 'single' ? 'Invite a new member to your team' : 'Add multiple members at once'}
                          </p>
                        </div>
                        {/* Toggle Switch */}
                        <div className="flex items-center gap-2.5 flex-shrink-0">
                          <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                            Single
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setAddMode(addMode === 'single' ? 'bulk' : 'single');
                            }}
                            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                              addMode === 'bulk' ? 'bg-indigo-600' : isDark ? 'bg-gray-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm ${
                                addMode === 'bulk' ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                            Bulk
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={closeAddPanel}
                    className={`transition-colors ${isDark ? 'text-white/50 hover:text-white' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="panel-content">
                {addMode === 'single' ? (
                <form id="add-user-form" onSubmit={handleAddUser} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-700'}`}>
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      data-field="firstName"
                      value={formData.firstName}
                      onChange={(e) => {
                        setFormData({ ...formData, firstName: e.target.value });
                        if (formErrors.firstName) {
                          setFormErrors(prev => ({ ...prev, firstName: '' }));
                        }
                      }}
                      className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                        formErrors.firstName 
                          ? (isDark ? 'border-red-500 focus:ring-red-500' : 'border-red-500 focus:ring-red-500')
                          : (isDark ? 'border-white/10' : 'border-slate-200')
                      } ${
                        isDark 
                          ? 'bg-white/5 text-white placeholder-gray-400' 
                          : 'bg-white text-slate-900 placeholder-slate-400'
                      } border`}
                    />
                    {formErrors.firstName && (
                      <p className={`text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{formErrors.firstName}</p>
                    )}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-700'}`}>
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      data-field="lastName"
                      value={formData.lastName}
                      onChange={(e) => {
                        setFormData({ ...formData, lastName: e.target.value });
                        if (formErrors.lastName) {
                          setFormErrors(prev => ({ ...prev, lastName: '' }));
                        }
                      }}
                      className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                        formErrors.lastName 
                          ? (isDark ? 'border-red-500 focus:ring-red-500' : 'border-red-500 focus:ring-red-500')
                          : (isDark ? 'border-white/10' : 'border-slate-200')
                      } ${
                        isDark 
                          ? 'bg-white/5 text-white placeholder-gray-400' 
                          : 'bg-white text-slate-900 placeholder-slate-400'
                      } border`}
                    />
                    {formErrors.lastName && (
                      <p className={`text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{formErrors.lastName}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-700'}`}>
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    data-field="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (formErrors.email) {
                        setFormErrors(prev => ({ ...prev, email: '' }));
                      }
                    }}
                    className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                      formErrors.email 
                        ? (isDark ? 'border-red-500 focus:ring-red-500' : 'border-red-500 focus:ring-red-500')
                        : (isDark ? 'border-white/10' : 'border-slate-200')
                    } ${
                      isDark 
                        ? 'bg-white/5 text-white placeholder-gray-400' 
                        : 'bg-white text-slate-900 placeholder-slate-400'
                    } border`}
                  />
                  {formErrors.email && (
                    <p className={`text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{formErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className={`block mb-1 ${isDark ? 'text-white' : 'text-slate-700'}`}>
                    Role <span className="text-red-500">*</span>
                  </label>
                  <div data-field="role">
                    <AntDropdown
                      value={formData.role}
                      onChange={(value) => {
                        setFormData({ ...formData, role: value });
                        if (formErrors.role) {
                          setFormErrors(prev => ({ ...prev, role: '' }));
                        }
                      }}
                      options={roles.map(role => ({
                        value: role.value,
                        label: role.label
                      }))}
                      placeholder="Select role"
                      showSearch={false}
                      allowClear={false}
                      className={formErrors.role ? 'error' : ''}
                    />
                  </div>
                  {formErrors.role && (
                    <p className={`text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{formErrors.role}</p>
                  )}
                </div>

                <div>
                  <label className={`block mb-1 ${isDark ? 'text-white' : 'text-slate-700'}`}>Position</label>
                  <div data-field="position">
                    {(() => {
                      const accessiblePositions = getAccessiblePositions();
                      const hasRealPositions = teamStructure && teamStructure.length > 0 && accessiblePositions.length > 0;
                      
                      // Check if we only have default positions (CEO, Manager, Developer)
                      const defaultPositionValues = ['CEO', 'Manager', 'Developer'];
                      const hasOnlyDefaults = accessiblePositions.length === 3 && 
                        accessiblePositions.every(pos => defaultPositionValues.includes(pos.value));
                      
                      if (!hasRealPositions || (teamStructure.length === 0 && hasOnlyDefaults)) {
                        return (
                          <button
                            type="button"
                            onClick={() => navigate('/dashboard/settings?section=team')}
                            className={`w-full px-4 py-2 rounded-lg border-2 border-dashed transition-all duration-200 flex items-center justify-center gap-2 ${
                              isDark
                                ? 'border-white/20 text-white hover:border-white/40 hover:bg-white/5'
                                : 'border-slate-300 text-slate-700 hover:border-indigo-400 hover:bg-indigo-50'
                            }`}
                          >
                            <Plus size={18} />
                            <span>Add position</span>
                          </button>
                        );
                      }
                      
                      return (
                        <AntDropdown
                          value={formData.position || undefined}
                          onChange={(value) => {
                            setFormData({ 
                              ...formData, 
                              position: value || '',
                              reportingManager: '', // Reset reporting manager when position changes
                              reportingManagers: [] // Reset reporting managers when position changes
                            });
                            if (formErrors.position) {
                              setFormErrors(prev => ({ ...prev, position: '' }));
                            }
                          }}
                          options={[
                            { value: '', label: 'Select Position' },
                            ...accessiblePositions.map(position => ({
                              value: position.value,
                              label: position.label
                            }))
                          ]}
                          placeholder="Select Position"
                          showSearch={true}
                          allowClear={true}
                          className={formErrors.position ? 'error' : ''}
                        />
                      );
                    })()}
                  </div>
                  {formErrors.position && (
                    <p className={`text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{formErrors.position}</p>
                  )}
                </div>

                {formData.position && (
                  <div>
                    <label className={`block mb-2 ${isDark ? 'text-white' : 'text-slate-700'}`}>Reporting Managers</label>
                    <AntMultiSelect
                      value={formData.reportingManagers || []}
                      onChange={(selectedManagers) => {
                        setFormData({
                          ...formData,
                          reportingManagers: selectedManagers,
                          reportingManager: selectedManagers.length > 0 ? selectedManagers[0] : ''
                        });
                      }}
                      options={getReportingManagersOptions(formData.position, selectedUser?._id, formData.reportingManagers || [])}
                      placeholder="Select reporting managers"
                      showSearch={true}
                      allowClear={true}
                      maxTagCount="responsive"
                    />
                    <p className={`text-xs mt-2 flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      <Lightbulb size={12} />
                      {getReportingManagerHelpText(formData.position)}. You can select multiple managers.
                    </p>
                  </div>
                )}

                {formData.role === 'org_user' && (
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-700'}`}>Permissions</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="addProject"
                          checked={formData.permissions.addProject}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: { ...formData.permissions, addProject: e.target.checked }
                          })}
                          className={`rounded text-blue-600 focus:ring-blue-500 ${
                            isDark ? 'border-white/20 bg-white/5' : 'border-slate-300 bg-white'
                          }`}
                        />
                        <label htmlFor="addProject" className={`text-sm ${isDark ? 'text-white' : 'text-slate-700'}`}>Add Project</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="addTeamMember"
                          checked={formData.permissions.addTeamMember}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: { ...formData.permissions, addTeamMember: e.target.checked }
                          })}
                          className={`rounded text-blue-600 focus:ring-blue-500 ${
                            isDark ? 'border-white/20 bg-white/5' : 'border-slate-300 bg-white'
                          }`}
                        />
                        <label htmlFor="addTeamMember" className={`text-sm ${isDark ? 'text-white' : 'text-slate-700'}`}>Add Team Member</label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="viewReports"
                          checked={formData.permissions.viewReports}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: { ...formData.permissions, viewReports: e.target.checked }
                          })}
                          className={`rounded text-blue-600 focus:ring-blue-500 ${
                            isDark ? 'border-white/20 bg-white/5' : 'border-slate-300 bg-white'
                          }`}
                        />
                        <label htmlFor="viewReports" className={`text-sm ${isDark ? 'text-white' : 'text-slate-700'}`}>View Reports</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="viewActivity"
                          checked={formData.permissions.viewActivity}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: { ...formData.permissions, viewActivity: e.target.checked }
                          })}
                          className={`rounded text-blue-600 focus:ring-blue-500 ${
                            isDark ? 'border-white/20 bg-white/5' : 'border-slate-300 bg-white'
                          }`}
                        />
                        <label htmlFor="viewActivity" className={`text-sm ${isDark ? 'text-white' : 'text-slate-700'}`}>View Activity</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="editProfile"
                          checked={formData.permissions.editProfile}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: { ...formData.permissions, editProfile: e.target.checked }
                          })}
                          className={`rounded text-blue-600 focus:ring-blue-500 ${
                            isDark ? 'border-white/20 bg-white/5' : 'border-slate-300 bg-white'
                          }`}
                        />
                        <label htmlFor="editProfile" className={`text-sm ${isDark ? 'text-white' : 'text-slate-700'}`}>Edit Profile</label>
                      </div>
                      {/* CRM access — only shown when org is whitelisted for CRM. Default off. */}
                      {showCrmPermission && (
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="crmAccess"
                            checked={!!formData.permissions.crmAccess}
                            onChange={(e) => setFormData({
                              ...formData,
                              permissions: { ...formData.permissions, crmAccess: e.target.checked }
                            })}
                            className={`rounded text-blue-600 focus:ring-blue-500 ${
                              isDark ? 'border-white/20 bg-white/5' : 'border-slate-300 bg-white'
                            }`}
                          />
                          <label htmlFor="crmAccess" className={`text-sm ${isDark ? 'text-white' : 'text-slate-700'}`}>CRM</label>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-700'}`}>Phone (Optional)</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                      isDark 
                        ? 'bg-white/5 border border-white/10 text-white placeholder-gray-400' 
                        : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400'
                    }`}
                  />
                </div>

                {/* <div>
                  <label className="block text-sm font-medium text-white mb-1">Invitation Method</label>
                  <div className="space-y-3">
                    <label className="flex items-start space-x-3 p-3 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-colors cursor-pointer">
                      <input
                        type="radio"
                        name="invitationMethod"
                        value="manual"
                        checked={formData.invitationMethod === 'manual'}
                        onChange={(e) => setFormData({ ...formData, invitationMethod: e.target.value })}
                        className="text-blue-600 focus:ring-blue-500 mt-1"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-white">Set Password Manually</span>
                        <p className="text-xs text-gray-400 mt-1">Create user account with a predefined password for immediate access</p>
                      </div>
                    </label>
                    
                    <label className="flex items-start space-x-3 p-3 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-colors cursor-pointer">
                      <input
                        type="radio"
                        name="invitationMethod"
                        value="email"
                        checked={formData.invitationMethod === 'email'}
                        onChange={(e) => setFormData({ ...formData, invitationMethod: e.target.value })}
                        className="text-blue-600 focus:ring-blue-500 mt-1"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-white">Send Invite Email</span>
                        <p className="text-xs text-gray-400 mt-1">Send a personalized invitation email with secure signup link to the user's email address</p>
                        <div className="mt-2">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={formData.sendEmailInvite}
                              onChange={(e) => setFormData({ ...formData, sendEmailInvite: e.target.checked })}
                              className="rounded border-white/20 bg-white/5 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-xs text-gray-300">Send invitation email with secure signup link</span>
                          </label>
                        </div>
                      </div>
                    </label>
                    
                    <label className="flex items-start space-x-3 p-3 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-colors cursor-pointer">
                      <input
                        type="radio"
                        name="invitationMethod"
                        value="link"
                        checked={formData.invitationMethod === 'link'}
                        onChange={(e) => setFormData({ ...formData, invitationMethod: e.target.value })}
                        className="text-blue-600 focus:ring-blue-500 mt-1"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-white">Invite via Link</span>
                        <p className="text-xs text-gray-400 mt-1">Generate a unique organization invitation link for direct signup</p>
                      </div>
                    </label>
                  </div>
                </div> */}

                {formData.invitationMethod === 'manual' && (
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-700'}`}>
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        required
                        data-field="password"
                        value={formData.password}
                        onChange={(e) => {
                          setFormData({ ...formData, password: e.target.value });
                          if (formErrors.password) {
                            setFormErrors(prev => ({ ...prev, password: '' }));
                          }
                        }}
                        placeholder="Set password for the new user"
                        className={`w-full px-3 py-2 pr-10 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                          formErrors.password 
                            ? (isDark ? 'border-red-500 focus:ring-red-500' : 'border-red-500 focus:ring-red-500')
                            : (isDark ? 'border-white/10' : 'border-slate-200')
                        } ${
                          isDark 
                            ? 'bg-white/5 text-white placeholder-gray-400' 
                            : 'bg-white text-slate-900 placeholder-slate-400'
                        } border`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors ${
                          isDark ? 'text-gray-400 hover:text-white' : 'text-slate-400 hover:text-slate-600'
                        }`}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {formErrors.password ? (
                      <p className={`text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{formErrors.password}</p>
                    ) : (
                      <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>This password will be used for both web and desktop application login</p>
                    )}
                  </div>
                )}

                {formData.invitationMethod === 'email' && (
                  <div>
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <h4 className="text-sm font-medium text-blue-400 mb-1">Email Invitation</h4>
                          <p className="text-xs text-blue-300">
                            An invitation email will be sent to {formData.email} with a secure signup link. The user will be able to set their own password and complete their account setup.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {formData.invitationMethod === 'link' && (
                  <div>
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <svg className="w-5 h-5 text-green-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <div>
                          <h4 className="text-sm font-medium text-green-400 mb-1">Organization Invitation Link</h4>
                          <p className="text-xs text-green-300">
                            A unique invitation link will be generated for your organization. Share this link with new team members for direct signup with organization pre-filled.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                </form>
                ) : (
                  // Bulk Form
                  <div className="space-y-5">
                    {/* CSV Upload Section */}
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 rounded-xl border-2 border-dashed transition-all duration-300 ${
                        isDark 
                          ? 'bg-white/5 border-indigo-400/30 hover:border-indigo-400/50' 
                          : 'bg-indigo-50 border-indigo-300 hover:border-indigo-400'
                      } backdrop-blur-sm`}
                    >
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${isDark ? 'bg-indigo-500/30' : 'bg-indigo-100'}`}>
                            <Upload className={`w-5 h-5 ${isDark ? 'text-indigo-300' : 'text-indigo-600'}`} />
                          </div>
                          <div>
                            <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-indigo-900'}`}>
                              Upload CSV/Excel File
                            </h4>
                            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-300' : 'text-indigo-700'}`}>
                              Upload a CSV or Excel file to bulk import team members
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <label
                            htmlFor="csv-upload"
                            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer shadow-sm ${
                              isUploadingCsv
                                ? 'opacity-50 cursor-not-allowed'
                                : 'bg-indigo-600 hover:bg-indigo-700 !text-white hover:shadow-md hover:scale-105'
                            }`}
                          >
                            {isUploadingCsv ? 'Uploading...' : 'Choose File'}
                          </label>
                          <input
                            id="csv-upload"
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                handleCsvUpload(file);
                              }
                              e.target.value = ''; // Reset input
                            }}
                            disabled={isUploadingCsv}
                            className="hidden"
                          />
                          
                          {/* Download Template Button */}
                          <button
                            type="button"
                            onClick={() => {
                              // Get actual available positions
                              const availablePositions = getAccessiblePositions().map(p => p.value);
                              const firstPosition = availablePositions[0] || 'Developer';
                              const secondPosition = availablePositions[1] || availablePositions[0] || 'Developer';
                              
                              // Get available managers for example
                              const availableManagers = allUsers.filter(u => 
                                u.role === 'org_admin' || u.position
                              );
                              const exampleManager = availableManagers.length > 0 
                                ? `${availableManagers[0].firstName} ${availableManagers[0].lastName}`
                                : '';
                              
                              // Create CSV template with actual available positions
                              const template = [
                                ['First Name', 'Last Name', 'Email', 'Role', 'Position', 'Phone', 'Reporting Manager', 'Password'],
                                ['John', 'Doe', 'john.doe@example.com', 'org_user', firstPosition, '+1234567890', exampleManager || '', ''],
                                ['Jane', 'Smith', 'jane.smith@example.com', 'org_user', secondPosition, '+1234567891', '', ''],
                              ];
                              
                              const csvContent = template.map(row => 
                                row.map(cell => `"${cell}"`).join(',')
                              ).join('\n');
                              
                              const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                              const link = document.createElement('a');
                              const url = URL.createObjectURL(blob);
                              link.setAttribute('href', url);
                              link.setAttribute('download', 'team_members_template.csv');
                              link.style.visibility = 'hidden';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              toast.success('Template downloaded with valid positions!');
                            }}
                            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
                              isDark
                                ? 'bg-white/10 text-white hover:bg-white/20'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                          >
                            <FileText size={14} />
                            Download Template
                          </button>
                        </div>
                      </div>

                      {/* Error Display */}
                      {csvUploadError && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-red-500/15 border border-red-500/30' : 'bg-red-50 border border-red-200'}`}
                        >
                          <div className="flex items-start gap-2">
                            <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                            <div className="flex-1">
                              <p className={`text-xs font-semibold ${isDark ? 'text-red-200' : 'text-red-900'}`}>
                                Upload Error
                              </p>
                              <p className={`text-xs mt-1 whitespace-pre-line ${isDark ? 'text-red-300' : 'text-red-700'}`}>
                                {csvUploadError}
                              </p>
                            </div>
                            <button
                              onClick={() => setCsvUploadError(null)}
                              className={`p-1 rounded ${isDark ? 'hover:bg-red-500/20' : 'hover:bg-red-100'}`}
                            >
                              <X size={14} className={isDark ? 'text-red-400' : 'text-red-600'} />
                            </button>
                          </div>
                        </motion.div>
                      )}

                      {/* Success Display */}
                      {csvUploadSuccess && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-green-500/15 border border-green-500/30' : 'bg-green-50 border border-green-200'}`}
                        >
                          <div className="flex items-start gap-2">
                            <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                            <div className="flex-1">
                              <p className={`text-xs font-semibold ${isDark ? 'text-green-200' : 'text-green-900'}`}>
                                Upload Successful!
                              </p>
                              <p className={`text-xs mt-1 ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                                {bulkUsers.length} user(s) imported successfully. Review the data below and click "Add X Member(s)" to save.
                              </p>
                            </div>
                            <button
                              onClick={() => setCsvUploadSuccess(false)}
                              className={`p-1 rounded ${isDark ? 'hover:bg-green-500/20' : 'hover:bg-green-100'}`}
                            >
                              <X size={14} className={isDark ? 'text-green-400' : 'text-green-600'} />
                            </button>
                          </div>
                        </motion.div>
                      )}

                      {/* Required Columns Info */}
                      <div className={`mt-3 p-3 rounded-lg ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white/50 border border-slate-200'}`}>
                        <p className={`text-xs font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-700'}`}>
                          Required Columns:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <span className={`text-xs px-2.5 py-1 rounded-md font-medium ${isDark ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-400/40' : 'bg-indigo-100 text-indigo-700 border border-indigo-200'}`}>
                            First Name *
                          </span>
                          <span className={`text-xs px-2.5 py-1 rounded-md font-medium ${isDark ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-400/40' : 'bg-indigo-100 text-indigo-700 border border-indigo-200'}`}>
                            Last Name *
                          </span>
                          <span className={`text-xs px-2.5 py-1 rounded-md font-medium ${isDark ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-400/40' : 'bg-indigo-100 text-indigo-700 border border-indigo-200'}`}>
                            Email *
                          </span>
                          <span className={`text-xs px-2.5 py-1 rounded-md font-medium ${isDark ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-400/40' : 'bg-indigo-100 text-indigo-700 border border-indigo-200'}`}>
                            Role
                          </span>
                          <span className={`text-xs px-2.5 py-1 rounded-md font-medium ${isDark ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-400/40' : 'bg-indigo-100 text-indigo-700 border border-indigo-200'}`}>
                            Position
                          </span>
                          <span className={`text-xs px-2.5 py-1 rounded-md font-medium ${isDark ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-400/40' : 'bg-indigo-100 text-indigo-700 border border-indigo-200'}`}>
                            Phone
                          </span>
                          <span className={`text-xs px-2.5 py-1 rounded-md font-medium ${isDark ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-400/40' : 'bg-indigo-100 text-indigo-700 border border-indigo-200'}`}>
                            Reporting Manager
                          </span>
                          <span className={`text-xs px-2.5 py-1 rounded-md font-medium ${isDark ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-400/40' : 'bg-indigo-100 text-indigo-700 border border-indigo-200'}`}>
                            Password
                          </span>
                        </div>
                        <p className={`text-xs mt-2 leading-relaxed ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                          💡 <strong>Note:</strong> Reporting Manager can be email or name (comma-separated for multiple). Position and Reporting Manager must match existing data. Department can be added manually in the table after import. Password is optional — set per-row in the CSV, or use "Common Password" below to apply one to all rows.
                        </p>
                      </div>
                    </motion.div>

                      {/* Info Banner */}
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 rounded-xl ${isDark ? 'bg-white/5 border border-indigo-400/20' : 'bg-indigo-50 border border-indigo-200'} backdrop-blur-sm`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${isDark ? 'bg-indigo-500/30' : 'bg-indigo-100'}`}>
                          <Sparkles className={`w-5 h-5 ${isDark ? 'text-indigo-300' : 'text-indigo-600'}`} />
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-indigo-900'}`}>
                            Add Multiple Members at Once
                          </p>
                          <p className={`text-xs mt-1.5 leading-relaxed ${isDark ? 'text-gray-300' : 'text-indigo-700'}`}>
                            Fill in the details for each member. Use "Apply to All" below to quickly assign common values to all rows at once.
                          </p>
                        </div>
                      </div>
                    </motion.div>

                    {/* Apply to All Section */}
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.1 }}
                      className={`p-5 rounded-xl border-2 border-dashed transition-all duration-300 ${isDark ? 'bg-white/5 border-indigo-400/30 hover:border-indigo-400/50' : 'bg-indigo-50 border-indigo-300 hover:border-indigo-400'} backdrop-blur-sm`}
                    >
                      <div className="flex items-center gap-3 mb-6">
                        <div className={`p-2 rounded-lg ${isDark ? 'bg-indigo-500/30' : 'bg-indigo-100'}`}>
                          <Zap className={`w-5 h-5 ${isDark ? 'text-indigo-300' : 'text-indigo-600'}`} />
                        </div>
                        <div>
                          <h4 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-indigo-900'}`}>
                            Apply to All Rows
                          </h4>
                          <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-300' : 'text-indigo-700'}`}>
                            Set common values for all members at once
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                        {/* Common Reporting Managers */}
                        <div className="sm:col-span-2 lg:col-span-2">
                          <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>
                            Reporting Managers
                          </label>
                          <div className="flex gap-2">
                            <div className="flex-1 min-w-0">
                              <AntMultiSelect
                                value={commonReportingManagers}
                                onChange={(selectedManagers) => setCommonReportingManagers(selectedManagers)}
                                options={allUsers
                                  .filter(u => u.role === 'org_admin' || u.position)
                                  .map(manager => ({
                                    value: manager._id,
                                    label: `${manager.firstName} ${manager.lastName} (${manager.position || manager.role})`
                                  }))}
                                placeholder="Select managers"
                                showSearch={true}
                                allowClear={true}
                                maxTagCount={1}
                                style={{ width: '100%' }}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={applyReportingManagersToAll}
                              disabled={commonReportingManagers.length === 0}
                              className={`px-3 md:px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 whitespace-nowrap flex-shrink-0 shadow-sm ${
                                commonReportingManagers.length === 0
                                  ? `${isDark ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`
                                  : 'bg-indigo-600 hover:bg-indigo-700 !text-white hover:shadow-md hover:scale-105'
                              }`}
                              title="Apply these reporting managers to all rows"
                            >
                              Apply
                            </button>
                          </div>
                        </div>

                        {/* Common Position */}
                        <div>
                          <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>
                            Position
                          </label>
                          <div className="flex gap-2">
                            <div className="flex-1 min-w-0">
                              {(() => {
                                const accessiblePositions = getAccessiblePositions();
                                const hasRealPositions = teamStructure && teamStructure.length > 0 && accessiblePositions.length > 0;
                                
                                // Check if we only have default positions (CEO, Manager, Developer)
                                const defaultPositionValues = ['CEO', 'Manager', 'Developer'];
                                const hasOnlyDefaults = accessiblePositions.length === 3 && 
                                  accessiblePositions.every(pos => defaultPositionValues.includes(pos.value));
                                
                                if (!hasRealPositions || (teamStructure.length === 0 && hasOnlyDefaults)) {
                                  return (
                                    <button
                                      type="button"
                                      onClick={() => navigate('/dashboard/settings?section=team')}
                                      className={`w-full px-4 py-2 rounded-lg border-2 border-dashed transition-all duration-200 flex items-center justify-center gap-2 ${
                                        isDark
                                          ? 'border-white/20 text-white hover:border-white/40 hover:bg-white/5'
                                          : 'border-slate-300 text-slate-700 hover:border-indigo-400 hover:bg-indigo-50'
                                      }`}
                                    >
                                      <Plus size={18} />
                                      <span>Add position</span>
                                    </button>
                                  );
                                }
                                
                                return (
                                  <AntDropdown
                                    value={commonPosition || undefined}
                                    onChange={(value) => setCommonPosition(value || '')}
                                    options={[
                                      { value: '', label: 'Select Position' },
                                      ...accessiblePositions.map(position => ({
                                        value: position.value,
                                        label: position.label
                                      }))
                                    ]}
                                    placeholder="Select position"
                                    showSearch={true}
                                    allowClear={true}
                                    style={{ width: '100%' }}
                                  />
                                );
                              })()}
                            </div>
                            {(() => {
                              const accessiblePositions = getAccessiblePositions();
                              const hasRealPositions = teamStructure && teamStructure.length > 0 && accessiblePositions.length > 0;
                              
                              // Check if we only have default positions (CEO, Manager, Developer)
                              const defaultPositionValues = ['CEO', 'Manager', 'Developer'];
                              const hasOnlyDefaults = accessiblePositions.length === 3 && 
                                accessiblePositions.every(pos => defaultPositionValues.includes(pos.value));
                              
                              // Only show Apply button if we have real positions
                              if (hasRealPositions && !(teamStructure.length === 0 && hasOnlyDefaults)) {
                                return (
                                  <button
                                    type="button"
                                    onClick={applyPositionToAll}
                                    disabled={!commonPosition}
                                    className={`px-3 md:px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 whitespace-nowrap flex-shrink-0 shadow-sm ${
                                      !commonPosition
                                        ? `${isDark ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`
                                        : 'bg-indigo-600 hover:bg-indigo-700 !text-white hover:shadow-md hover:scale-105'
                                    }`}
                                    title="Apply this position to all rows"
                                  >
                                    Apply
                                  </button>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>

                        {/* Common Role */}
                        <div>
                          <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>
                            Role
                          </label>
                          <div className="flex gap-2">
                            <div className="flex-1 min-w-0">
                              <AntDropdown
                                value={commonRole || 'org_user'}
                                onChange={(value) => setCommonRole(value || 'org_user')}
                                options={roles.map(role => ({
                                  value: role.value,
                                  label: role.label
                                }))}
                                placeholder="Select Role"
                                showSearch={true}
                                allowClear={false}
                                style={{ width: '100%' }}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => applyRoleToAll(commonRole || 'org_user')}
                              className="px-3 md:px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 whitespace-nowrap flex-shrink-0 shadow-sm bg-indigo-600 hover:bg-indigo-700 !text-white hover:shadow-md hover:scale-105"
                              title="Apply selected role to all rows"
                            >
                              Apply
                            </button>
                          </div>
                        </div>

                        {/* Common Password */}
                        <div className="sm:col-span-2 lg:col-span-2 xl:col-span-2">
                          <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>
                            Common Password
                          </label>
                          <div className="flex gap-2">
                            <div className="flex-1 min-w-0">
                              <input
                                type="text"
                                value={commonPassword}
                                onChange={(e) => setCommonPassword(e.target.value)}
                                placeholder="Leave blank for auto-generated"
                                autoComplete="new-password"
                                className={`w-full px-3 py-2 text-xs md:text-sm rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 ${
                                  isDark
                                    ? 'bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:ring-indigo-500/50 focus:border-indigo-500/50 hover:bg-white/10'
                                    : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-indigo-500 focus:border-indigo-500 hover:border-slate-300'
                                }`}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={applyPasswordToAll}
                              disabled={!commonPassword.trim()}
                              className={`px-3 md:px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 whitespace-nowrap flex-shrink-0 shadow-sm ${
                                !commonPassword.trim()
                                  ? `${isDark ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`
                                  : 'bg-indigo-600 hover:bg-indigo-700 !text-white hover:shadow-md hover:scale-105'
                              }`}
                              title="Apply this password to all rows (overrides per-row values)"
                            >
                              Apply
                            </button>
                          </div>
                          <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                            Applies to every row. Per-row passwords from the CSV or table take effect only when left blank here.
                          </p>
                        </div>

                        {/* Common Permissions */}
                        <div className="sm:col-span-2 lg:col-span-4 xl:col-span-5">
                          <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                            Permissions (for org_user role)
                          </label>
                          <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-slate-200'}`}>
                            <div className="flex items-center gap-3 md:gap-4 mb-3 flex-wrap">
                              <div className={`flex items-center gap-2 p-1.5 rounded-lg transition-colors cursor-pointer ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                                <input
                                  type="checkbox"
                                  id="common-addProject"
                                  checked={commonPermissions.addProject}
                                  onChange={(e) => setCommonPermissions({ ...commonPermissions, addProject: e.target.checked })}
                                  className={`w-4 h-4 rounded transition-all cursor-pointer ${
                                    isDark 
                                      ? 'border-white/30 bg-white/5 text-indigo-500 focus:ring-indigo-500/50' 
                                      : 'border-slate-300 bg-white text-indigo-600 focus:ring-indigo-500'
                                  }`}
                                />
                                <label htmlFor="common-addProject" className={`text-xs cursor-pointer ${isDark ? 'text-white' : 'text-slate-700'}`}>
                                  Add Project
                                </label>
                              </div>
                              <div className={`flex items-center gap-2 p-1.5 rounded-lg transition-colors cursor-pointer ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                                <input
                                  type="checkbox"
                                  id="common-addTeamMember"
                                  checked={commonPermissions.addTeamMember}
                                  onChange={(e) => setCommonPermissions({ ...commonPermissions, addTeamMember: e.target.checked })}
                                  className={`w-4 h-4 rounded transition-all cursor-pointer ${
                                    isDark 
                                      ? 'border-white/30 bg-white/5 text-indigo-500 focus:ring-indigo-500/50' 
                                      : 'border-slate-300 bg-white text-indigo-600 focus:ring-indigo-500'
                                  }`}
                                />
                                <label htmlFor="common-addTeamMember" className={`text-xs cursor-pointer ${isDark ? 'text-white' : 'text-slate-700'}`}>
                                  Add Team Member
                                </label>
                              </div>
                              <div className={`flex items-center gap-2 p-1.5 rounded-lg transition-colors cursor-pointer ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                                <input
                                  type="checkbox"
                                  id="common-viewReports"
                                  checked={commonPermissions.viewReports}
                                  onChange={(e) => setCommonPermissions({ ...commonPermissions, viewReports: e.target.checked })}
                                  className={`w-4 h-4 rounded transition-all cursor-pointer ${
                                    isDark 
                                      ? 'border-white/30 bg-white/5 text-indigo-500 focus:ring-indigo-500/50' 
                                      : 'border-slate-300 bg-white text-indigo-600 focus:ring-indigo-500'
                                  }`}
                                />
                                <label htmlFor="common-viewReports" className={`text-xs cursor-pointer ${isDark ? 'text-white' : 'text-slate-700'}`}>
                                  View Reports
                                </label>
                              </div>
                              <div className={`flex items-center gap-2 p-1.5 rounded-lg transition-colors cursor-pointer ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                                <input
                                  type="checkbox"
                                  id="common-viewActivity"
                                  checked={commonPermissions.viewActivity}
                                  onChange={(e) => setCommonPermissions({ ...commonPermissions, viewActivity: e.target.checked })}
                                  className={`w-4 h-4 rounded transition-all cursor-pointer ${
                                    isDark 
                                      ? 'border-white/30 bg-white/5 text-indigo-500 focus:ring-indigo-500/50' 
                                      : 'border-slate-300 bg-white text-indigo-600 focus:ring-indigo-500'
                                  }`}
                                />
                                <label htmlFor="common-viewActivity" className={`text-xs cursor-pointer ${isDark ? 'text-white' : 'text-slate-700'}`}>
                                  View Activity
                                </label>
                              </div>
                              <div className={`flex items-center gap-2 p-1.5 rounded-lg transition-colors cursor-pointer ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                                <input
                                  type="checkbox"
                                  id="common-editProfile"
                                  checked={commonPermissions.editProfile}
                                  onChange={(e) => setCommonPermissions({ ...commonPermissions, editProfile: e.target.checked })}
                                  className={`w-4 h-4 rounded transition-all cursor-pointer ${
                                    isDark
                                      ? 'border-white/30 bg-white/5 text-indigo-500 focus:ring-indigo-500/50'
                                      : 'border-slate-300 bg-white text-indigo-600 focus:ring-indigo-500'
                                  }`}
                                />
                                <label htmlFor="common-editProfile" className={`text-xs cursor-pointer ${isDark ? 'text-white' : 'text-slate-700'}`}>
                                  Edit Profile
                                </label>
                              </div>
                              {/* CRM access in bulk Apply-to-All — only shown when org is whitelisted */}
                              {showCrmPermission && (
                                <div className={`flex items-center gap-2 p-1.5 rounded-lg transition-colors cursor-pointer ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                                  <input
                                    type="checkbox"
                                    id="common-crmAccess"
                                    checked={!!commonPermissions.crmAccess}
                                    onChange={(e) => setCommonPermissions({ ...commonPermissions, crmAccess: e.target.checked })}
                                    className={`w-4 h-4 rounded transition-all cursor-pointer ${
                                      isDark
                                        ? 'border-white/30 bg-white/5 text-indigo-500 focus:ring-indigo-500/50'
                                        : 'border-slate-300 bg-white text-indigo-600 focus:ring-indigo-500'
                                    }`}
                                  />
                                  <label htmlFor="common-crmAccess" className={`text-xs cursor-pointer ${isDark ? 'text-white' : 'text-slate-700'}`}>
                                    CRM
                                  </label>
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={applyPermissionsToAll}
                              className="ml-auto px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 whitespace-nowrap shadow-sm bg-indigo-600 hover:bg-indigo-700 !text-white hover:shadow-md hover:scale-105"
                              title="Apply these permissions to all org_user rows"
                            >
                              Apply Permissions
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Quick Actions */}
                      <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                            Quick Actions:
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const department = prompt('Enter department name to apply to all rows:');
                              if (department !== null) {
                                applyDepartmentToAll(department);
                              }
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                              isDark
                                ? 'bg-white/10 text-gray-200 hover:bg-white/20 hover:scale-105'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200 hover:scale-105'
                            }`}
                          >
                            Apply Department
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm('Clear all reporting managers from all rows?')) {
                                setBulkUsers(
                                  bulkUsers.map((user) => ({
                                    ...user,
                                    reportingManagers: [],
                                  }))
                                );
                                setCommonReportingManagers([]);
                                toast.success('Cleared all reporting managers');
                              }
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                              isDark
                                ? 'bg-red-500/25 text-red-300 hover:bg-red-500/35 hover:scale-105'
                                : 'bg-red-50 text-red-600 hover:bg-red-100 hover:scale-105'
                            }`}
                          >
                            Clear All Managers
                          </button>
                        </div>
                      </div>
                    </motion.div>

                    {/* Bulk Users Table */}
                    {(() => {
                      // Calculate pagination
                      const startIndex = (bulkUsersCurrentPage - 1) * bulkUsersPageSize;
                      const endIndex = startIndex + bulkUsersPageSize;
                      const paginatedBulkUsers = bulkUsers.slice(startIndex, endIndex);
                      const totalPages = Math.ceil(bulkUsers.length / bulkUsersPageSize);
                      
                      return (
                        <>
                          <div className={`overflow-x-auto -mx-2 md:-mx-4 rounded-xl border transition-all duration-300 ${isDark ? 'border-white/10 shadow-lg shadow-black/20 bg-black/30' : 'border-slate-200 shadow-md bg-white'}`}>
                            <div className="inline-block min-w-full align-middle">
                              <table className="min-w-full divide-y divide-opacity-20">
                                <thead className={`${isDark ? 'bg-gradient-to-r from-white/10 to-white/5' : 'bg-gradient-to-r from-slate-50 to-slate-100'} backdrop-blur-sm`}>
                                  <tr>
                                    <th className={`text-left px-3 md:px-4 py-3 text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'} sticky left-0 ${isDark ? 'bg-gradient-to-r from-white/10 to-white/5' : 'bg-gradient-to-r from-slate-50 to-slate-100'} z-10 backdrop-blur-sm`}>
                                      Details
                                    </th>
                                    <th className={`text-left px-3 md:px-4 py-3 text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'} whitespace-nowrap`}>
                                      First Name *
                                    </th>
                                    <th className={`text-left px-3 md:px-4 py-3 text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'} whitespace-nowrap`}>
                                      Last Name *
                                    </th>
                                    <th className={`text-left px-3 md:px-4 py-3 text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'} whitespace-nowrap`}>
                                      Email *
                                    </th>
                                    <th className={`text-left px-3 md:px-4 py-3 text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'} whitespace-nowrap`}>
                                      Role
                                    </th>
                                    <th className={`text-left px-3 md:px-4 py-3 text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'} whitespace-nowrap`}>
                                      Position
                                    </th>
                                    <th className={`text-left px-3 md:px-4 py-3 text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'} whitespace-nowrap`}>
                                      Phone
                                    </th>
                                    <th className={`text-left px-3 md:px-4 py-3 text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'} whitespace-nowrap`}>
                                      Password
                                    </th>
                                    <th className={`text-left px-3 md:px-4 py-3 text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-slate-700'} sticky right-0 ${isDark ? 'bg-gradient-to-r from-white/10 to-white/5' : 'bg-gradient-to-r from-slate-50 to-slate-100'} z-10 backdrop-blur-sm`}>
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                              <tbody>
                                {paginatedBulkUsers.map((bulkUser, index) => (
                            <React.Fragment key={bulkUser.id}>
                              <motion.tr
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.2, delay: index * 0.05 }}
                                className={`border-b transition-all duration-200 ${isDark ? 'border-white/10 bg-black/50 hover:bg-white/10' : 'border-slate-100 bg-white hover:bg-slate-50/50'} cursor-pointer group`}
                                onClick={() => toggleBulkUserExpanded(bulkUser.id)}
                              >
                                <td className={`px-3 md:px-4 py-3 sticky left-0 transition-colors ${isDark ? 'bg-black/50 group-hover:bg-white/10' : 'bg-white group-hover:bg-slate-50/50'} z-5`}>
                                  <button
                                    type="button"
                                    className={`p-1.5 rounded-lg transition-all duration-200 ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'} ${bulkUser.expanded ? 'rotate-90' : ''}`}
                                  >
                                    <ChevronDown size={16} className={`transition-colors ${isDark ? 'text-gray-400 group-hover:text-white' : 'text-slate-600 group-hover:text-slate-900'}`} />
                                  </button>
                                </td>
                                <td className="px-3 md:px-4 py-3 min-w-[120px]">
                                  <input
                                    type="text"
                                    value={bulkUser.firstName}
                                    onChange={(e) =>
                                      updateBulkUser(bulkUser.id, 'firstName', e.target.value)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="John"
                                    className={`w-full px-3 py-2 text-xs md:text-sm rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 ${
                                      isDark
                                        ? 'bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:ring-indigo-500/50 focus:border-indigo-500/50 hover:bg-white/10'
                                        : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-indigo-500 focus:border-indigo-500 hover:border-slate-300'
                                    }`}
                                  />
                                </td>
                                <td className="px-3 md:px-4 py-3 min-w-[120px]">
                                  <input
                                    type="text"
                                    value={bulkUser.lastName}
                                    onChange={(e) =>
                                      updateBulkUser(bulkUser.id, 'lastName', e.target.value)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="Doe"
                                    className={`w-full px-3 py-2 text-xs md:text-sm rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 ${
                                      isDark
                                        ? 'bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:ring-indigo-500/50 focus:border-indigo-500/50 hover:bg-white/10'
                                        : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-indigo-500 focus:border-indigo-500 hover:border-slate-300'
                                    }`}
                                  />
                                </td>
                                <td className="px-3 md:px-4 py-3 min-w-[180px]">
                                  <input
                                    type="email"
                                    value={bulkUser.email}
                                    onChange={(e) =>
                                      updateBulkUser(bulkUser.id, 'email', e.target.value)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="john@example.com"
                                    className={`w-full px-3 py-2 text-xs md:text-sm rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 ${
                                      isDark
                                        ? 'bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:ring-indigo-500/50 focus:border-indigo-500/50 hover:bg-white/10'
                                        : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-indigo-500 focus:border-indigo-500 hover:border-slate-300'
                                    }`}
                                  />
                                </td>
                                <td className="px-3 md:px-4 py-3 min-w-[110px]">
                                  <select
                                    value={bulkUser.role}
                                    onChange={(e) =>
                                      updateBulkUser(bulkUser.id, 'role', e.target.value)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    className={`w-full px-3 py-2 text-xs md:text-sm rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 cursor-pointer ${
                                      isDark
                                        ? 'bg-white/5 border border-white/10 text-white focus:ring-indigo-500/50 focus:border-indigo-500/50 hover:bg-white/10'
                                        : 'bg-white border border-slate-200 text-slate-900 focus:ring-indigo-500 focus:border-indigo-500 hover:border-slate-300'
                                    }`}
                                  >
                                    {roles.map((role) => (
                                      <option key={role.value} value={role.value}>
                                        {role.label}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-3 md:px-4 py-3 min-w-[130px]">
                                  <input
                                    type="text"
                                    value={bulkUser.position || ''}
                                    onChange={(e) =>
                                      updateBulkUser(bulkUser.id, 'position', e.target.value)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="Developer"
                                    className={`w-full px-3 py-2 text-xs md:text-sm rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 ${
                                      isDark
                                        ? 'bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:ring-indigo-500/50 focus:border-indigo-500/50 hover:bg-white/10'
                                        : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-indigo-500 focus:border-indigo-500 hover:border-slate-300'
                                    }`}
                                  />
                                </td>
                                {/* <td className="px-3 md:px-4 py-3 min-w-[130px]">
                                  <input
                                    type="text"
                                    value={bulkUser.department || ''}
                                    onChange={(e) =>
                                      updateBulkUser(bulkUser.id, 'department', e.target.value)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="Engineering"
                                    className={`w-full px-3 py-2 text-xs md:text-sm rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 ${
                                      isDark
                                        ? 'bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:ring-indigo-500/50 focus:border-indigo-500/50 hover:bg-white/10'
                                        : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-indigo-500 focus:border-indigo-500 hover:border-slate-300'
                                    }`}
                                  />
                                </td> */}
                                <td className="px-3 md:px-4 py-3 min-w-[140px]">
                                  <input
                                    type="tel"
                                    value={bulkUser.phone || ''}
                                    onChange={(e) =>
                                      updateBulkUser(bulkUser.id, 'phone', e.target.value)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="+1234567890"
                                    className={`w-full px-3 py-2 text-xs md:text-sm rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 ${
                                      isDark
                                        ? 'bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:ring-indigo-500/50 focus:border-indigo-500/50 hover:bg-white/10'
                                        : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-indigo-500 focus:border-indigo-500 hover:border-slate-300'
                                    }`}
                                  />
                                </td>
                                <td className="px-3 md:px-4 py-3 min-w-[160px]">
                                  <input
                                    type="text"
                                    value={bulkUser.password || ''}
                                    onChange={(e) =>
                                      updateBulkUser(bulkUser.id, 'password', e.target.value)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="Auto-generated if empty"
                                    autoComplete="new-password"
                                    className={`w-full px-3 py-2 text-xs md:text-sm rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 ${
                                      isDark
                                        ? 'bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:ring-indigo-500/50 focus:border-indigo-500/50 hover:bg-white/10'
                                        : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-indigo-500 focus:border-indigo-500 hover:border-slate-300'
                                    }`}
                                  />
                                </td>
                                <td className={`px-3 md:px-4 py-3 sticky right-0 transition-colors ${isDark ? 'bg-black/50 group-hover:bg-white/10' : 'bg-white group-hover:bg-slate-50/50'} z-5`}>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeBulkUserRow(bulkUser.id);
                                    }}
                                    disabled={bulkUsers.length === 1}
                                    className={`p-2 rounded-lg transition-all duration-200 ${
                                      bulkUsers.length === 1
                                        ? 'opacity-50 cursor-not-allowed'
                                        : `${isDark ? 'hover:bg-red-500/20 hover:scale-110' : 'hover:bg-red-50 hover:scale-110'}`
                                    } ${isDark ? 'text-red-400' : 'text-red-600'}`}
                                  >
                                    <X size={16} />
                                  </button>
                                </td>
                              </motion.tr>
                              
                              {/* Expanded Row - Reporting Managers & Permissions */}
                              {bulkUser.expanded && (
                                <motion.tr 
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className={`${isDark ? 'bg-gradient-to-r from-white/5 to-white/3' : 'bg-gradient-to-r from-slate-50 to-slate-100'}`}
                                >
                                  <td colSpan={9} className="p-5">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                      {/* Reporting Managers */}
                                      <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-slate-200'}`}>
                                        <label className={`block text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-700'}`}>
                                          Reporting Managers
                                        </label>
                                        {bulkUser.position ? (
                                          <>
                                            <AntMultiSelect
                                              value={bulkUser.reportingManagers || []}
                                              onChange={(selectedManagers) =>
                                                updateBulkUserReportingManagers(bulkUser.id, selectedManagers)
                                              }
                                              options={getReportingManagersOptions(bulkUser.position, bulkUser.id, bulkUser.reportingManagers || [])}
                                              placeholder="Select reporting managers"
                                              showSearch={true}
                                              allowClear={true}
                                              maxTagCount="responsive"
                                            />
                                            <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                              {getReportingManagerHelpText(bulkUser.position)}. You can select multiple managers.
                                            </p>
                                          </>
                                        ) : (
                                          <>
                                            <AntMultiSelect
                                              value={bulkUser.reportingManagers || []}
                                              onChange={(selectedManagers) =>
                                                updateBulkUserReportingManagers(bulkUser.id, selectedManagers)
                                              }
                                              options={(() => {
                                                // Get filtered managers
                                                const filteredManagers = allUsers.filter(u => u.role === 'org_admin' || u.position);
                                                const filteredIds = new Set(filteredManagers.map(m => String(m._id)));
                                                
                                                // Include already-selected managers that might not be in filtered list
                                                const selectedIds = (bulkUser.reportingManagers || []).map(id => String(id));
                                                const missingManagers = selectedIds
                                                  .filter(id => !filteredIds.has(id))
                                                  .map(id => allUsers.find(u => String(u._id) === id))
                                                  .filter(Boolean);
                                                
                                                const allOptions = [...filteredManagers, ...missingManagers];
                                                const uniqueManagers = Array.from(
                                                  new Map(allOptions.map(m => [String(m._id), m])).values()
                                                );
                                                
                                                return uniqueManagers.map(manager => ({
                                                  value: manager._id,
                                                  label: `${manager.firstName} ${manager.lastName} (${manager.position || manager.role})`
                                                }));
                                              })()}
                                              placeholder="Select reporting managers"
                                              showSearch={true}
                                              allowClear={true}
                                              maxTagCount="responsive"
                                            />
                                            <div className={`mt-2 p-2 rounded-lg ${isDark ? 'bg-yellow-500/10 border border-yellow-500/20' : 'bg-yellow-50 border border-yellow-200'}`}>
                                              <p className={`text-xs flex items-center gap-1.5 ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                                                <AlertCircle size={14} />
                                                Position not set. Set position first for better hierarchy-based suggestions.
                                              </p>
                                            </div>
                                            {bulkUser.reportingManagers && bulkUser.reportingManagers.length > 0 && (
                                              <div className={`mt-2 p-2 rounded-lg ${isDark ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-200'}`}>
                                                <p className={`text-xs flex items-center gap-1.5 ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                                                  <CheckCircle size={14} />
                                                  {bulkUser.reportingManagers.length} reporting manager(s) assigned
                                                </p>
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </div>

                                      {/* Permissions (only for org_user) */}
                                      {bulkUser.role === 'org_user' && (
                                        <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-slate-200'}`}>
                                          <label className={`block text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-700'}`}>
                                            Permissions
                                          </label>
                                          <div className="space-y-2.5">
                                            <div className={`flex items-center space-x-2.5 p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                                              <input
                                                type="checkbox"
                                                checked={bulkUser.permissions?.addProject || false}
                                                onChange={(e) =>
                                                  updateBulkUserPermission(bulkUser.id, 'addProject', e.target.checked)
                                                }
                                                className={`w-4 h-4 rounded transition-all cursor-pointer ${
                                                  isDark 
                                                    ? 'border-white/30 bg-white/5 text-indigo-500 focus:ring-indigo-500/50' 
                                                    : 'border-slate-300 bg-white text-indigo-600 focus:ring-indigo-500'
                                                }`}
                                              />
                                              <label className={`text-sm cursor-pointer ${isDark ? 'text-white' : 'text-slate-700'}`}>
                                                Add Project
                                              </label>
                                            </div>
                                            <div className={`flex items-center space-x-2.5 p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                                              <input
                                                type="checkbox"
                                                checked={bulkUser.permissions?.addTeamMember || false}
                                                onChange={(e) =>
                                                  updateBulkUserPermission(bulkUser.id, 'addTeamMember', e.target.checked)
                                                }
                                                className={`w-4 h-4 rounded transition-all cursor-pointer ${
                                                  isDark 
                                                    ? 'border-white/30 bg-white/5 text-indigo-500 focus:ring-indigo-500/50' 
                                                    : 'border-slate-300 bg-white text-indigo-600 focus:ring-indigo-500'
                                                }`}
                                              />
                                              <label className={`text-sm cursor-pointer ${isDark ? 'text-white' : 'text-slate-700'}`}>
                                                Add Team Member
                                              </label>
                                            </div>
                                            <div className={`flex items-center space-x-2.5 p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                                              <input
                                                type="checkbox"
                                                checked={bulkUser.permissions?.viewReports !== false}
                                                onChange={(e) =>
                                                  updateBulkUserPermission(bulkUser.id, 'viewReports', e.target.checked)
                                                }
                                                className={`w-4 h-4 rounded transition-all cursor-pointer ${
                                                  isDark 
                                                    ? 'border-white/30 bg-white/5 text-indigo-500 focus:ring-indigo-500/50' 
                                                    : 'border-slate-300 bg-white text-indigo-600 focus:ring-indigo-500'
                                                }`}
                                              />
                                              <label className={`text-sm cursor-pointer ${isDark ? 'text-white' : 'text-slate-700'}`}>
                                                View Reports
                                              </label>
                                            </div>
                                            <div className={`flex items-center space-x-2.5 p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                                              <input
                                                type="checkbox"
                                                checked={bulkUser.permissions?.viewActivity !== false}
                                                onChange={(e) =>
                                                  updateBulkUserPermission(bulkUser.id, 'viewActivity', e.target.checked)
                                                }
                                                className={`w-4 h-4 rounded transition-all cursor-pointer ${
                                                  isDark 
                                                    ? 'border-white/30 bg-white/5 text-indigo-500 focus:ring-indigo-500/50' 
                                                    : 'border-slate-300 bg-white text-indigo-600 focus:ring-indigo-500'
                                                }`}
                                              />
                                              <label className={`text-sm cursor-pointer ${isDark ? 'text-white' : 'text-slate-700'}`}>
                                                View Activity
                                              </label>
                                            </div>
                                            <div className={`flex items-center space-x-2.5 p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                                              <input
                                                type="checkbox"
                                                checked={bulkUser.permissions?.editProfile !== false}
                                                onChange={(e) =>
                                                  updateBulkUserPermission(bulkUser.id, 'editProfile', e.target.checked)
                                                }
                                                className={`w-4 h-4 rounded transition-all cursor-pointer ${
                                                  isDark 
                                                    ? 'border-white/30 bg-white/5 text-indigo-500 focus:ring-indigo-500/50' 
                                                    : 'border-slate-300 bg-white text-indigo-600 focus:ring-indigo-500'
                                                }`}
                                              />
                                              <label className={`text-sm cursor-pointer ${isDark ? 'text-white' : 'text-slate-700'}`}>
                                                Edit Profile
                                              </label>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </motion.tr>
                              )}
                              </React.Fragment>
                            ))}
                              </tbody>
                            </table>
                            </div>
                          </div>
                          
                          {/* Pagination - Only show if more than pageSize items */}
                          {bulkUsers.length > bulkUsersPageSize && (
                            <div className={`mt-4 p-4 rounded-lg ${isDark ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                              <AntPagination
                                current={bulkUsersCurrentPage}
                                pageSize={bulkUsersPageSize}
                                total={bulkUsers.length}
                                onChange={(page, size) => {
                                  setBulkUsersCurrentPage(page);
                                  if (size !== bulkUsersPageSize) {
                                    setBulkUsersPageSize(size);
                                    setBulkUsersCurrentPage(1);
                                  }
                                }}
                                onShowSizeChange={(current, size) => {
                                  setBulkUsersPageSize(size);
                                  setBulkUsersCurrentPage(1);
                                }}
                                showSizeChanger
                                showTotal={(total, range) => (
                                  <span className={isDark ? 'text-gray-300' : 'text-slate-700'}>
                                    {range[0]}-{range[1]} of {total} members
                                  </span>
                                )}
                                pageSizeOptions={['10', '20', '50', '100']}
                                showQuickJumper={bulkUsers.length > 50}
                              />
                            </div>
                          )}
                        </>
                      );
                    })()}

                    <motion.button
                      type="button"
                      onClick={addBulkUserRow}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex items-center gap-2 px-5 py-2 !text-white rounded-xl transition-all duration-200 text-sm font-medium shadow-sm bg-indigo-600 hover:bg-indigo-700 !text-white hover:shadow-md"
                    >
                      <UserPlus size={18} />
                      Add Row
                    </motion.button>

                    <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                      <div className="flex items-start gap-2">
                        <Lightbulb className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                        <div className={`text-xs leading-relaxed ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                          <p className="font-medium mb-1">💡 Tips:</p>
                          <ul className="list-disc list-inside space-y-0.5 ml-1">
                            <li>* Required fields: First Name, Last Name, Email</li>
                            <li>Click on any row to expand and configure reporting managers & permissions</li>
                            <li>All users will be created and will receive invitation emails</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="panel-footer">
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeAddPanel}
                    className="btn-secondary"
                    style={{ borderRadius: 10 }}
                  >
                    Cancel
                  </button>
                  {addMode === 'single' ? (
                  <button
                    type="submit"
                    form="add-user-form"
                    onClick={handleAddUser}
                    disabled={addUserLoading}
                    className="btn-primary"
                    style={{ borderRadius: 10 }}
                  >
                    {addUserLoading ? (
                      <>
                        <Spin size="small" />
                        <span>Adding...</span>
                      </>
                    ) : (
                      'Add Team Member'
                    )}
                  </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleBulkAddUsers}
                      disabled={bulkUploadLoading}
                      className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 !text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {bulkUploadLoading ? (
                        <>
                          <span className="inline-block animate-spin mr-2">⏳</span>
                          Adding...
                        </>
                      ) : (
                        `Add ${bulkUsers.length} Member(s)`
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Invitation Link Modal */}
      {showInvitationLink && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-md bg-black/80 backdrop-blur-xl border border-white/20 shadow-2xl rounded-xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Invitation Link Created</h3>
                  <p className="text-sm text-gray-400">Share this link with the new team member</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowInvitationLink(false);
                  setInvitationLink('');
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-white mb-2">Organization Invitation Link</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={invitationLink}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                  />
                  <button
                    type="button"
                    onClick={copyInvitationLink}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-green-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-green-400 mb-1">Organization-Specific Link</h4>
                    <p className="text-xs text-green-300">
                      This link is unique to your organization. When users click this link, they'll be automatically assigned to your organization and can set their own password during signup.
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => {
                    setShowInvitationLink(false);
                    setInvitationLink('');
                    setShowAddModal(false);
                  }}
                  className="px-6 py-2.5 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors border border-white/20 font-medium"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Modal Styles */}
      <style>{`
        .payment-modal .ant-modal-content {
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
        }
        .payment-modal .ant-modal-body {
          padding: 0 !important;
        }
        .payment-modal .ant-modal-close {
          color: ${isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)'} !important;
          top: 16px !important;
          right: 16px !important;
        }
        .payment-modal .ant-modal-close:hover {
          color: ${isDark ? '#ffffff' : '#000000'} !important;
          background: ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'} !important;
        }
        .upgrade-plan-modal .ant-modal-content {
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
        }
        .upgrade-plan-modal .ant-modal-body {
          padding: 0 !important;
        }
        .upgrade-plan-modal .ant-modal-close {
          color: ${isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)'} !important;
          top: 16px !important;
          right: 16px !important;
        }
        .upgrade-plan-modal .ant-modal-close:hover {
          color: ${isDark ? '#ffffff' : '#000000'} !important;
          background: ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'} !important;
        }
        .ant-input-number {
          background: ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'} !important;
          border: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'} !important;
        }
        .ant-input-number-input {
          color: ${isDark ? '#ffffff' : '#000000'} !important;
          background: transparent !important;
        }
        .ant-input-number-handler {
          border-color: ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'} !important;
        }
        .ant-input-number-handler:hover {
          color: ${isDark ? '#ffffff' : '#000000'} !important;
        }
        .ant-statistic-content {
          color: ${isDark ? '#ffffff' : '#000000'} !important;
        }
        .ant-alert {
          border-radius: 12px !important;
        }
        .ant-alert-info {
          background: rgba(59, 130, 246, 0.1) !important;
          border-color: rgba(59, 130, 246, 0.2) !important;
        }
        .ant-alert-warning {
          background: ${isDark ? 'rgba(251, 191, 36, 0.1)' : 'rgba(249, 115, 22, 0.1)'} !important;
          border-color: ${isDark ? 'rgba(251, 191, 36, 0.2)' : 'rgba(249, 115, 22, 0.3)'} !important;
        }
        .ant-alert-message {
          color: ${isDark ? '#ffffff' : '#000000'} !important;
        }
        .ant-alert-description {
          color: ${isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)'} !important;
        }
        .ant-card {
          border-radius: 16px !important;
        }
        .ant-badge {
          color: ${isDark ? '#ffffff' : '#000000'} !important;
        }
      `}</style>

      {/* Upgrade Plan Modal - For Free Plan Users */}
      <Modal
        open={showUpgradeModal}
        onCancel={() => {
          setShowUpgradeModal(false);
          setSelectedUpgradePlan(null);
          setUpgradeMemberCount({}); // Reset member count when modal closes
        }}
        footer={null}
        closable={true}
        width={900}
        className="upgrade-plan-modal"
        styles={{
          mask: { backgroundColor: isDark ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(8px)' },
          content: { backgroundColor: 'transparent', boxShadow: 'none', padding: 0 }
        }}
      >
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className={`${
              isDark 
                ? 'bg-gradient-to-br from-zinc-900/95 via-zinc-800/95 to-zinc-900/95 border border-white/10' 
                : 'bg-white border border-gray-200 shadow-xl'
            } rounded-2xl p-8 backdrop-blur-xl`}
          >
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-center mb-8"
            >
              <div className="flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-yellow-400 mr-2" />
                <Title level={2} className={`!mb-0 !text-3xl !font-bold ${isDark ? '!text-white' : '!text-gray-900'}`}>
                  Upgrade Your Plan
                </Title>
              </div>
              <Paragraph className={`!text-base !mb-0 ${isDark ? '!text-gray-400' : '!text-gray-600'}`}>
                You've used all 5 free slots. Upgrade to add more team members.
              </Paragraph>
            </motion.div>

            {/* Plan Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Standard Plan */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                whileHover={{ scale: 1.02, y: -5 }}
              >
                <Card
                  className={`h-full border-2 transition-all cursor-pointer ${
                    selectedUpgradePlan === 'standard'
                      ? `border-blue-400 ${isDark ? 'bg-blue-400/10 shadow-xl shadow-blue-500/30' : 'bg-blue-50 shadow-xl shadow-blue-500/10'}`
                      : isDark 
                        ? 'border-white/20 bg-white/5 hover:border-white/40'
                        : 'border-gray-200 bg-white hover:border-gray-300 shadow-md'
                  }`}
                  style={{ borderRadius: '16px' }}
                  onClick={() => setSelectedUpgradePlan('standard')}
                >
                  <div className="text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-blue-500/20' : 'bg-blue-100'
                    }`}>
                      <Zap className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <Title level={4} className={`!mb-2 !text-xl ${isDark ? '!text-white' : '!text-gray-900'}`}>Standard</Title>
                    <div className="mb-6">
                      <span className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatPrice('standard')}</span>
                      <span className={`text-sm ml-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>/member/month</span>
                    </div>
                    <Space direction="vertical" size="middle" className="w-full mb-6 text-left">
                      <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">All features</span>
                      </div>
                      <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Unlimited team members</span>
                      </div>
                      <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Advanced analytics</span>
                      </div>
                      <div className={`flex items-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        <X className={`w-4 h-4 mr-2 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        <span className="text-sm">No Org Intelligence</span>
                      </div>
                    </Space>
                    
                    {/* Member Count Input Section */}
                    <div className={`mb-4 p-3 rounded-lg border ${
                      isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="mb-2">
                        <Text className={`text-xs block mb-1.5 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Number of Team Members
                        </Text>
                        {(() => {
                          // Calculate minimum member count (current used - 1 for admin)
                          const currentSlots = organization?.usedMemberSlots || 5;
                          const minMemberCount = Math.max(1, currentSlots - 1);
                          const memberCount = upgradeMemberCount['standard'] || minMemberCount;
                          const totalSeats = memberCount + 1; // +1 for admin
                          const monthlyPrice = totalSeats * getPrice('standard');
                          
                          return (
                            <>
                              <InputNumber
                                min={minMemberCount}
                                max={1000}
                                value={memberCount}
                                onChange={(value) => {
                                  setUpgradeMemberCount(prev => ({
                                    ...prev,
                                    'standard': value || minMemberCount
                                  }));
                                }}
                                controls={true}
                                className="w-full"
                                style={{ width: '100%' }}
                                size="small"
                              />
                              <Text className={`text-xs block mt-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Currently using: {currentSlots} slots (1 admin + {currentSlots - 1} members)
                              </Text>
                              <Text className={`text-xs block mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Total seats: {totalSeats} (1 admin + {memberCount} members)
                              </Text>
                              <Divider className="!my-2 !border-gray-300/50" />
                              <div className="text-center">
                                <Text className={`text-xs block ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                  Monthly Total
                                </Text>
                                <Text className={`text-lg font-bold block ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                  {symbol}{monthlyPrice.toFixed(2)}/month
                                </Text>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <Button
                      type={selectedUpgradePlan === 'standard' ? 'primary' : 'default'}
                      size="large"
                      block
                      onClick={(e) => {
                        e.stopPropagation();
                        // If plan is not selected, just select it (don't start checkout)
                        if (selectedUpgradePlan !== 'standard') {
                          setSelectedUpgradePlan('standard');
                        } else {
                          // Plan is already selected, proceed to checkout
                          handleUpgradePlanSelection('standard');
                        }
                      }}
                      disabled={processingUpgradePlan !== null}
                      loading={processingUpgradePlan === 'standard'}
                      className={selectedUpgradePlan === 'standard' ? 'bg-blue-600 hover:bg-blue-700' : (isDark ? 'bg-white/10 hover:bg-white/20 border-white/20' : 'bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-800')}
                      style={{ height: '48px', borderRadius: '8px' }}
                    >
                      {processingUpgradePlan === 'standard' ? 'Processing...' : selectedUpgradePlan === 'standard' ? 'Proceed to Payment' : 'Select Standard'}
                    </Button>
                  </div>
                </Card>
              </motion.div>

              {/* Premium Plan */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                whileHover={{ scale: 1.02, y: -5 }}
              >
                <Card
                  className={`h-full border-2 transition-all cursor-pointer ${
                    selectedUpgradePlan === 'premium'
                      ? `border-purple-400 ${isDark ? 'bg-purple-400/10 shadow-xl shadow-purple-500/30' : 'bg-purple-50 shadow-xl shadow-purple-500/10'}`
                      : isDark 
                        ? 'border-white/20 bg-white/5 hover:border-white/40'
                        : 'border-gray-200 bg-white hover:border-gray-300 shadow-md'
                  }`}
                  style={{ borderRadius: '16px' }}
                  onClick={() => setSelectedUpgradePlan('premium')}
                >
                  <div className="text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-purple-500/20' : 'bg-purple-100'
                    }`}>
                      <Crown className={`w-8 h-8 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                    </div>
                    <Title level={4} className={`!mb-2 !text-xl ${isDark ? '!text-white' : '!text-gray-900'}`}>Premium</Title>
                    <div className="mb-6">
                      <span className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatPrice('premium')}</span>
                      <span className={`text-sm ml-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>/member/month</span>
                    </div>
                    <Space direction="vertical" size="middle" className="w-full mb-6 text-left">
                      <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">All features</span>
                      </div>
                      <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Org Intelligence</span>
                      </div>
                      <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Priority support</span>
                      </div>
                      <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">Advanced AI</span>
                      </div>
                    </Space>
                    
                    {/* Member Count Input Section */}
                    <div className={`mb-4 p-3 rounded-lg border ${
                      isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="mb-2">
                        <Text className={`text-xs block mb-1.5 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Number of Team Members
                        </Text>
                        {(() => {
                          // Calculate minimum member count (current used - 1 for admin)
                          const currentSlots = organization?.usedMemberSlots || 5;
                          const minMemberCount = Math.max(1, currentSlots - 1);
                          const memberCount = upgradeMemberCount['premium'] || minMemberCount;
                          const totalSeats = memberCount + 1; // +1 for admin
                          const monthlyPrice = totalSeats * getPrice('premium');
                          
                          return (
                            <>
                              <InputNumber
                                min={minMemberCount}
                                max={1000}
                                value={memberCount}
                                onChange={(value) => {
                                  setUpgradeMemberCount(prev => ({
                                    ...prev,
                                    'premium': value || minMemberCount
                                  }));
                                }}
                                controls={true}
                                className="w-full"
                                style={{ width: '100%' }}
                                size="small"
                              />
                              <Text className={`text-xs block mt-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Currently using: {currentSlots} slots (1 admin + {currentSlots - 1} members)
                              </Text>
                              <Text className={`text-xs block mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Total seats: {totalSeats} (1 admin + {memberCount} members)
                              </Text>
                              <Divider className="!my-2 !border-gray-300/50" />
                              <div className="text-center">
                                <Text className={`text-xs block ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                  Monthly Total
                                </Text>
                                <Text className={`text-lg font-bold block ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                  {symbol}{monthlyPrice.toFixed(2)}/month
                                </Text>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <Button
                      type={selectedUpgradePlan === 'premium' ? 'primary' : 'default'}
                      size="large"
                      block
                      onClick={(e) => {
                        e.stopPropagation();
                        // If plan is not selected, just select it (don't start checkout)
                        if (selectedUpgradePlan !== 'premium') {
                          setSelectedUpgradePlan('premium');
                        } else {
                          // Plan is already selected, proceed to checkout
                          handleUpgradePlanSelection('premium');
                        }
                      }}
                      disabled={processingUpgradePlan !== null}
                      loading={processingUpgradePlan === 'premium'}
                      className={selectedUpgradePlan === 'premium' ? 'bg-purple-600 hover:bg-purple-700' : (isDark ? 'bg-white/10 hover:bg-white/20 border-white/20' : 'bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-800')}
                      style={{ height: '48px', borderRadius: '8px' }}
                    >
                      {processingUpgradePlan === 'premium' ? 'Processing...' : selectedUpgradePlan === 'premium' ? 'Proceed to Payment' : 'Select Premium'}
                    </Button>
                  </div>
                </Card>
              </motion.div>
            </div>

            {/* Coupon Code Section - Show when plan is selected */}
            {(selectedUpgradePlan === 'standard' || selectedUpgradePlan === 'premium') && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={`mb-6 p-4 rounded-lg border ${
                  isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <label className={`block text-sm font-medium mb-2 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Have a coupon or promotion code? (Optional)
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setCouponCode(value);
                        
                        // Clear previous timeout
                        if (couponTimeoutRef.current) {
                          clearTimeout(couponTimeoutRef.current);
                        }
                        
                        // Verify after user stops typing (debounce)
                        if (value.trim() === '') {
                          setActualCouponId(null);
                          setCouponValid(null);
                          setCouponDiscount(null);
                        } else {
                          // Debounce verification - wait 800ms after user stops typing
                          couponTimeoutRef.current = setTimeout(() => {
                            verifyCouponCode(value);
                          }, 800);
                        }
                      }}
                      onBlur={() => {
                        if (couponCode.trim() !== '') {
                          verifyCouponCode(couponCode);
                        }
                      }}
                      placeholder="Enter coupon or promotion code"
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 ${
                        couponValid === true
                          ? isDark 
                            ? 'bg-green-500/10 border-green-500/50 text-white focus:ring-green-500/50 focus:border-green-500/50' 
                            : 'bg-green-50 border-green-500 text-gray-900 focus:ring-green-500 focus:border-green-500'
                          : couponValid === false
                            ? isDark 
                              ? 'bg-red-500/10 border-red-500/50 text-white focus:ring-red-500/50 focus:border-red-500/50' 
                              : 'bg-red-50 border-red-500 text-gray-900 focus:ring-red-500 focus:border-red-500'
                          : isDark 
                            ? 'bg-white/5 border-white/20 text-white placeholder-gray-500 focus:ring-blue-500/50 focus:border-blue-500/50' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                      style={{ height: '40px' }}
                      disabled={couponVerifying}
                    />
                    {couponVerifying && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                      </div>
                    )}
                    {couponValid === true && !couponVerifying && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <Check className="h-5 w-5 text-green-500" />
                      </div>
                    )}
                    {couponValid === false && !couponVerifying && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <X className="h-5 w-5 text-red-500" />
                      </div>
                    )}
                  </div>
                  {couponCode && (
                    <button
                      type="button"
                      onClick={() => {
                        setCouponCode('');
                        setActualCouponId(null);
                        setCouponValid(null);
                        setCouponDiscount(null);
                      }}
                      className={`px-3 py-2 rounded-lg border transition-colors ${
                        isDark 
                          ? 'bg-white/10 border-white/20 text-gray-300 hover:bg-white/20' 
                          : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {couponValid === true && couponDiscount && (
                  <Text className={`text-sm mt-2 block font-medium ${
                    isDark ? 'text-green-400' : 'text-green-600'
                  }`}>
                    ✅ Valid! {couponDiscount} will be applied at checkout
                  </Text>
                )}
                {couponValid === false && (
                  <Text className={`text-sm mt-2 block ${
                    isDark ? 'text-red-400' : 'text-red-600'
                  }`}>
                    ❌ Invalid coupon code. Please check and try again.
                  </Text>
                )}
                {couponCode && couponValid === null && !couponVerifying && (
                  <Text className={`text-xs mt-2 block ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    💰 Enter coupon code to verify
                  </Text>
                )}
              </motion.div>
            )}

            {/* Info */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center"
            >
              <Text className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                💡 After upgrading, you can purchase additional slots to add more team members
              </Text>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </Modal>

      {/* Payment Modal - Professional UI with Ant Design & Framer Motion */}
      <Modal
        open={showPaymentModal && pendingUser}
        onCancel={() => setShowPaymentModal(false)}
        footer={null}
        closable={true}
        width={600}
        className="payment-modal"
        styles={{
          mask: { backgroundColor: isDark ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(8px)' },
          content: { backgroundColor: 'transparent', boxShadow: 'none', padding: 0 }
        }}
      >
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className={`${
              isDark 
                ? 'bg-gradient-to-br from-zinc-900/95 via-zinc-800/95 to-zinc-900/95 border border-white/10' 
                : 'bg-white border border-gray-200 shadow-xl'
            } rounded-2xl p-5 backdrop-blur-xl`}
          >
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-center mb-4"
            >
              <div className="flex items-center justify-center mb-2">
                {pendingUser?.needsAdditionalSlots ? (
                  <ShoppingCart className="w-6 h-6 text-yellow-400 mr-2" />
                ) : (
                  <CreditCard className="w-6 h-6 text-blue-400 mr-2" />
                )}
                <Title level={3} className={`!mb-0 !text-xl !font-bold ${isDark ? '!text-white' : '!text-gray-900'}`}>
                  {pendingUser?.needsAdditionalSlots ? 'Purchase Additional Slots' : 'Activate User'}
                </Title>
            </div>
              <Paragraph className={`!text-sm !mb-0 ${isDark ? '!text-gray-400' : '!text-gray-600'}`}>
                {pendingUser?.needsAdditionalSlots 
                  ? 'You need to purchase additional slots to activate this team member'
                  : 'Payment required to activate this team member'}
              </Paragraph>
            </motion.div>

            {/* User Info Card - Compact */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className={`mb-3 ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`} style={{ borderRadius: '12px', padding: '12px' }}>
                <div className="flex items-center space-x-3 mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isDark ? 'bg-blue-500/20' : 'bg-blue-100'
                  }`}>
                    <UserPlus className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                  <div className="flex-1">
                    <Title level={5} className={`!mb-0 !text-sm !font-bold ${isDark ? '!text-white' : '!text-gray-900'}`}>{pendingUser?.name}</Title>
                    <Text className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{pendingUser?.email}</Text>
                </div>
              </div>

                <Divider className={`!my-2 ${isDark ? '!border-white/10' : '!border-gray-200'}`} />

                <div className="flex justify-between items-center text-xs">
                  <Text className={isDark ? 'text-gray-300' : 'text-gray-700'}>Plan:</Text>
                  <Badge 
                    color={organization?.orgPlanSelection === 'premium' ? 'purple' : organization?.orgPlanSelection === 'standard' ? 'blue' : 'default'}
                    text={
                      <Text className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {organization?.orgPlanSelection === 'free' ? `Free (${formatPrice('free')})` : 
                     organization?.orgPlanSelection === 'standard' ? `Standard (${formatPrice('standard')}/month)` : 
                     organization?.orgPlanSelection === 'premium' ? `Premium (${formatPrice('premium')}/month)` : `Standard (${formatPrice('standard')}/month)`}
                      </Text>
                    }
                  />
                </div>
                <div className="flex justify-between items-center text-xs mt-2">
                  <Text className={isDark ? 'text-gray-300' : 'text-gray-700'}>Cost per slot:</Text>
                  <Text className={`font-semibold text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {organization?.orgPlanSelection === 'premium' ? `${formatPrice('premium')}/month` : `${formatPrice('standard')}/month`}
                  </Text>
                </div>
              </Card>
            </motion.div>

            {/* Additional Slots Section - Compact */}
            {pendingUser?.needsAdditionalSlots && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-2 mb-3"
              >
                {/* Current Slots Status - Compact */}
                <Card className={`${isDark ? 'border-blue-500/20 bg-blue-500/10' : 'border-blue-200 bg-blue-50'}`} style={{ borderRadius: '12px', padding: '12px' }}>
                  <div className="flex items-center mb-2">
                    <TrendingUp className={`w-4 h-4 mr-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    <Title level={5} className={`!mb-0 !text-xs ${isDark ? '!text-blue-300' : '!text-blue-700'}`}>Current Slots Status</Title>
                </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Statistic
                      title={<Text className={`text-xs ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>Paid</Text>}
                      value={organization?.paidMemberSlots || 0}
                      valueStyle={{ color: isDark ? '#ffffff' : '#1e40af', fontSize: '16px', fontWeight: 'bold' }}
                    />
                    <Statistic
                      title={<Text className={`text-xs ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>Used</Text>}
                      value={organization?.usedMemberSlots || 0}
                      valueStyle={{ color: isDark ? '#ffffff' : '#1e40af', fontSize: '16px', fontWeight: 'bold' }}
                    />
                    <Statistic
                      title={<Text className={`text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>Remaining</Text>}
                      value={organization?.remainingSlots || 0}
                      valueStyle={{ color: isDark ? '#f87171' : '#dc2626', fontSize: '16px', fontWeight: 'bold' }}
                    />
              </div>
                </Card>

                {/* Purchase Section - Compact */}
                <Card className={`${isDark ? 'border-yellow-500/20 bg-yellow-500/10' : 'border-blue-200 bg-blue-50'}`} style={{ borderRadius: '12px', padding: '12px' }}>
                  <div className="flex items-center mb-2">
                    <Sparkles className={`w-4 h-4 mr-2 ${isDark ? 'text-yellow-400' : 'text-blue-600'}`} />
                    <Title level={5} className={`!mb-0 !text-xs ${isDark ? '!text-yellow-300' : '!text-blue-700'}`}>Purchase Additional Slots</Title>
            </div>

                  <div className="space-y-2">
                    <div>
                      <Text className={`font-medium block mb-1 text-xs ${isDark ? 'text-yellow-300' : 'text-blue-700'}`}>Number of Slots:</Text>
                      <InputNumber
                        min={1}
                        max={100}
                        value={additionalSlotsQuantity}
                        onChange={(value) => setAdditionalSlotsQuantity(value || 1)}
                        controls={true}
                        size="middle"
                        className="w-full"
                        style={{ width: '100%' }}
                      />
                    </div>

                    <Divider className={`!my-2 ${isDark ? '!border-yellow-500/20' : '!border-blue-200'}`} />

                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-xs">
                        <Text className={isDark ? 'text-yellow-200' : 'text-blue-700'}>Slots:</Text>
                        <Text className={`font-semibold text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>{additionalSlotsQuantity} {additionalSlotsQuantity === 1 ? 'slot' : 'slots'}</Text>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <Text className={isDark ? 'text-yellow-200' : 'text-blue-700'}>Cost per Slot:</Text>
                        <Text className={`font-semibold text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {symbol}{getPrice(organization?.orgPlanSelection || 'standard')}/month
                        </Text>
                      </div>
                      <Divider className={`!my-1.5 ${isDark ? '!border-yellow-500/20' : '!border-blue-200'}`} />
                      <div className={`flex justify-between items-center p-2 rounded-lg border ${
                        isDark ? 'bg-yellow-500/10 border-yellow-500/20' : 'bg-blue-100 border-blue-200'
                      }`}>
                        <Text className={`font-bold text-xs ${isDark ? 'text-yellow-300' : 'text-blue-700'}`}>Total Cost:</Text>
                        <Text className={`font-bold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {symbol}{getPrice(organization?.orgPlanSelection || 'standard') * additionalSlotsQuantity}/month
                        </Text>
                      </div>
                      <div className={`flex justify-between items-center p-2 rounded-lg border ${
                        isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-100 border-green-200'
                      }`}>
                        <Text className={`font-bold text-xs ${isDark ? 'text-green-300' : 'text-green-700'}`}>Total After Purchase:</Text>
                        <Text className={`font-bold text-base ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                          {(organization?.paidMemberSlots || 0) + additionalSlotsQuantity} slots
                        </Text>
                      </div>
                    </div>
                    
                    {/* Coupon Code Section */}
                    <div className="mt-3">
                      <label className={`block text-xs font-medium mb-1.5 ${
                        isDark ? 'text-yellow-300' : 'text-blue-700'
                      }`}>
                        Have a coupon or promotion code? (Optional)
                      </label>
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={couponCode}
                            onChange={(e) => {
                              const value = e.target.value.toUpperCase();
                              setCouponCode(value);
                              
                              // Clear previous timeout
                              if (couponTimeoutRef.current) {
                                clearTimeout(couponTimeoutRef.current);
                              }
                              
                              // Verify after user stops typing (debounce)
                              if (value.trim() === '') {
                                setActualCouponId(null);
                                setCouponValid(null);
                                setCouponDiscount(null);
                              } else {
                                // Debounce verification - wait 800ms after user stops typing
                                couponTimeoutRef.current = setTimeout(() => {
                                  verifyCouponCode(value);
                                }, 800);
                              }
                            }}
                            onBlur={() => {
                              if (couponCode.trim() !== '') {
                                verifyCouponCode(couponCode);
                              }
                            }}
                            placeholder="Enter coupon code"
                            className={`w-full px-3 py-1.5 text-xs rounded-lg border focus:outline-none focus:ring-2 ${
                              couponValid === true
                                ? isDark 
                                  ? 'bg-green-500/10 border-green-500/50 text-white focus:ring-green-500/50 focus:border-green-500/50' 
                                  : 'bg-green-50 border-green-500 text-gray-900 focus:ring-green-500 focus:border-green-500'
                                : couponValid === false
                                  ? isDark 
                                    ? 'bg-red-500/10 border-red-500/50 text-white focus:ring-red-500/50 focus:border-red-500/50' 
                                    : 'bg-red-50 border-red-500 text-gray-900 focus:ring-red-500 focus:border-red-500'
                                  : isDark 
                                    ? 'bg-white/5 border-white/20 text-white placeholder-gray-500 focus:ring-blue-500/50 focus:border-blue-500/50' 
                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500'
                            }`}
                            style={{ height: '32px', fontSize: '12px' }}
                            disabled={couponVerifying}
                          />
                          {couponVerifying && (
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                            </div>
                          )}
                          {couponValid === true && !couponVerifying && (
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                              <Check className="h-4 w-4 text-green-500" />
                            </div>
                          )}
                          {couponValid === false && !couponVerifying && (
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                              <X className="h-4 w-4 text-red-500" />
                            </div>
                          )}
                        </div>
                        {couponCode && (
                          <button
                            type="button"
                            onClick={() => {
                              setCouponCode('');
                              setActualCouponId(null);
                              setCouponValid(null);
                              setCouponDiscount(null);
                            }}
                            className={`px-2 py-1.5 text-xs rounded-lg border transition-colors ${
                              isDark 
                                ? 'bg-white/10 border-white/20 text-gray-300 hover:bg-white/20' 
                                : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      {couponValid === true && couponDiscount && (
                        <Text className={`text-xs mt-1 block font-medium ${
                          isDark ? 'text-green-400' : 'text-green-600'
                        }`}>
                          ✅ Valid! {couponDiscount} will be applied
                        </Text>
                      )}
                      {couponValid === false && (
                        <Text className={`text-xs mt-1 block ${
                          isDark ? 'text-red-400' : 'text-red-600'
                        }`}>
                          ❌ Invalid coupon code
                        </Text>
                      )}
                    </div>
                  </div>
                </Card>

                {/* Warning Alert - Compact */}
                <Alert
                  message={<span className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Slots Exhausted</span>}
                  description={<span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>You have used all available slots. This purchase will add {additionalSlotsQuantity} {additionalSlotsQuantity === 1 ? 'additional slot' : 'additional slots'}.</span>}
                  type="warning"
                  showIcon
                  className={isDark ? 'border-yellow-500/20 bg-yellow-500/10' : 'border-orange-300 bg-orange-50'}
                  style={{ 
                    borderRadius: '8px', 
                    padding: '8px',
                    backgroundColor: isDark ? 'rgba(251, 191, 36, 0.1)' : 'rgba(255, 247, 237, 1)',
                    borderColor: isDark ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 146, 60, 0.3)'
                  }}
                />
              </motion.div>
            )}

            {/* Info Card - Compact */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mb-3"
            >
              <Alert
                message={<span className={`text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>Plan Features</span>}
                description={<span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{organization?.orgPlanSelection === 'free' ? 'All users in your org must be on the Free plan.' : organization?.orgPlanSelection === 'standard' ? 'Includes all features except Organizational Intelligence.' : 'Includes all features including Organizational Intelligence.'}</span>}
                type="info"
                showIcon
                className={isDark ? 'border-blue-500/20 bg-blue-500/10' : 'border-blue-200 bg-blue-50'}
                style={{ borderRadius: '8px', padding: '8px' }}
              />
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex space-x-2"
            >
              <Button
                size="middle"
                onClick={() => setShowPaymentModal(false)}
                disabled={loading}
                className={`flex-1 ${
                  isDark 
                    ? 'bg-white/10 hover:bg-white/20 border-white/20 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-800'
                }`}
                style={{ height: '40px', borderRadius: '8px' }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="middle"
                onClick={handlePaymentSubmit}
                disabled={loading}
                loading={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                style={{ height: '40px', borderRadius: '8px' }}
                icon={<CreditCard className="w-3 h-3" />}
              >
                {loading ? 'Processing...' : 'Proceed to Payment'}
              </Button>
            </motion.div>

            <Text className={`text-xs block text-center mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              🔒 You'll be redirected to secure {organization?.paymentGateway === 'razorpay' ? 'Razorpay' : 'Stripe'} checkout
            </Text>
          </motion.div>
        </AnimatePresence>
      </Modal>

      {/* Edit User Panel */}
      {showEditModal && selectedUser && (
        <>
          {/* Backdrop */}
          <div
            className={`panel-backdrop ${isDark ? 'dark' : 'light'}`}
            onClick={closeEditPanel}
          />

          {/* Slide-in Panel */}
          <div className={`slide-panel ${isEditPanelAnimating ? 'open' : ''}`} style={{ width: 'min(600px, 100vw)' }}>
            <div className="panel-container">
              {/* Header */}
              <div className="panel-header">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div style={{
                      width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isDark ? 'var(--accent-glow)' : 'rgba(108, 99, 255, 0.08)',
                    }}>
                      <Edit style={{ width: 20, height: 20, color: 'var(--accent-primary)' }} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Edit Team Member</h3>
                      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '2px 0 0' }}>Update member information</p>
                    </div>
                  </div>
                  <button
                    onClick={closeEditPanel}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)' }}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="panel-content">
                <form id="edit-user-form" onSubmit={handleEditUser}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">First Name</label>
                    <input
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-white/20 focus:border-white/20 text-white placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">Last Name</label>
                    <input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-white/20 focus:border-white/20 text-white placeholder-gray-400"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-white mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-white/20 focus:border-white/20 text-white placeholder-gray-400"
                  />
                </div>

                {(user.role === 'main_admin' || user.role === 'org_admin') && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-white mb-1">Role</label>
                    <AntDropdown
                      value={formData.role}
                      onChange={(value) => setFormData({ ...formData, role: value })}
                      options={roles.map(role => ({
                        value: role.value,
                        label: role.label
                      }))}
                      placeholder="Select role"
                      showSearch={false}
                      allowClear={false}
                    />
                  </div>
                )}

                <div className="mt-4">
                  <label className="block text-sm font-medium text-white mb-1">Password (Optional)</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Leave empty to keep current password"
                      className="w-full px-3 py-2 pr-10 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-white/20 focus:border-white/20 text-white placeholder-gray-400"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Enter new password to change it, or leave empty to keep current password</p>
                </div>

                {(user.role === 'org_admin' || user.role === 'main_admin') && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-700'}`}>Stealth Mode</label>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Enable stealth mode for this user</p>
                      </div>
                      <Switch
                        checked={formData.stealth_mode || false}
                        onChange={(checked) => setFormData({ ...formData, stealth_mode: checked })}
                      />
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <label className="block text-sm font-medium text-white mb-1">Position</label>
                  {(() => {
                    const accessiblePositions = getAccessiblePositions();
                    const hasRealPositions = teamStructure && teamStructure.length > 0 && accessiblePositions.length > 0;
                    
                    // Check if we only have default positions (CEO, Manager, Developer)
                    const defaultPositionValues = ['CEO', 'Manager', 'Developer'];
                    const hasOnlyDefaults = accessiblePositions.length === 3 && 
                      accessiblePositions.every(pos => defaultPositionValues.includes(pos.value));
                    
                    if (!hasRealPositions || (teamStructure.length === 0 && hasOnlyDefaults)) {
                      return (
                        <button
                          type="button"
                          onClick={() => navigate('/dashboard/settings?section=team')}
                          className={`w-full px-4 py-2 rounded-lg border-2 border-dashed transition-all duration-200 flex items-center justify-center gap-2 ${
                            isDark
                              ? 'border-white/20 text-white hover:border-white/40 hover:bg-white/5'
                              : 'border-slate-300 text-slate-700 hover:border-indigo-400 hover:bg-indigo-50'
                          }`}
                        >
                          <Plus size={18} />
                          <span>Add position</span>
                        </button>
                      );
                    }
                    
                    return (
                      <AntDropdown
                        value={formData.position || undefined}
                        onChange={(value) => {
                          setFormData({ 
                            ...formData, 
                            position: value || '',
                            reportingManager: '', // Reset reporting manager when position changes
                            reportingManagers: [] // Reset reporting managers when position changes
                          });
                        }}
                        options={[
                          { value: '', label: 'Select Position' },
                          ...accessiblePositions.map(position => ({
                            value: position.value,
                            label: position.label
                          }))
                        ]}
                        placeholder="Select Position"
                        showSearch={true}
                        allowClear={true}
                      />
                    );
                  })()}
                </div>

                {formData.position && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-white mb-2">
                      Reporting Managers <span className="text-red-400">*</span>
                    </label>
                    <AntMultiSelect
                      value={formData.reportingManagers || []}
                      onChange={(selectedManagers) => {
                        setFormData({
                          ...formData,
                          reportingManagers: selectedManagers,
                          reportingManager: selectedManagers.length > 0 ? selectedManagers[0] : ''
                        });
                        if (selectedManagers.length > 0) {
                          setFormErrors(prev => ({ ...prev, reportingManagers: '' }));
                        }
                      }}
                      options={getReportingManagersOptions(formData.position, selectedUser?._id, formData.reportingManagers || [])}
                      placeholder="Select reporting managers"
                      showSearch={true}
                      allowClear={true}
                      maxTagCount="responsive"
                    />
                    {formErrors.reportingManagers && (
                      <p className="text-xs mt-1 text-red-400">{formErrors.reportingManagers}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Lightbulb size={12} />
                      {getReportingManagerHelpText(formData.position)}. You can select multiple managers.
                    </p>
                  </div>
                )}

                {formData.role === 'org_user' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-white mb-3">Permissions</label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="edit-addProject"
                          checked={formData.permissions.addProject}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: { ...formData.permissions, addProject: e.target.checked }
                          })}
                          className="rounded border-white/20 bg-white/5 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="edit-addProject" className="text-sm text-white">Add Project</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="edit-addTeamMember"
                          checked={formData.permissions.addTeamMember}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: { ...formData.permissions, addTeamMember: e.target.checked }
                          })}
                          className="rounded border-white/20 bg-white/5 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="edit-addTeamMember" className="text-sm text-white">Add Team Member</label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="edit-viewReports"
                          checked={formData.permissions.viewReports}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: { ...formData.permissions, viewReports: e.target.checked }
                          })}
                          className="rounded border-white/20 bg-white/5 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="edit-viewReports" className="text-sm text-white">View Reports</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="edit-viewActivity"
                          checked={formData.permissions.viewActivity}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: { ...formData.permissions, viewActivity: e.target.checked }
                          })}
                          className="rounded border-white/20 bg-white/5 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="edit-viewActivity" className="text-sm text-white">View Activity</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="edit-editProfile"
                          checked={formData.permissions.editProfile}
                          onChange={(e) => setFormData({
                            ...formData,
                            permissions: { ...formData.permissions, editProfile: e.target.checked }
                          })}
                          className="rounded border-white/20 bg-white/5 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="edit-editProfile" className="text-sm text-white">Edit Profile</label>
                      </div>
                      {/* CRM access (Edit User panel) — gated on org whitelist */}
                      {showCrmPermission && (
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="edit-crmAccess"
                            checked={!!formData.permissions.crmAccess}
                            onChange={(e) => setFormData({
                              ...formData,
                              permissions: { ...formData.permissions, crmAccess: e.target.checked }
                            })}
                            className="rounded border-white/20 bg-white/5 text-blue-600 focus:ring-blue-500"
                          />
                          <label htmlFor="edit-crmAccess" className="text-sm text-white">CRM</label>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <label className="block text-sm font-medium text-white mb-1">Phone (Optional)</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-white/20 focus:border-white/20 text-white placeholder-gray-400"
                  />
                </div>

                </form>
              </div>

              {/* Footer */}
              <div className="panel-footer">
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeEditPanel}
                    className="btn-secondary"
                    style={{ borderRadius: 10 }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="edit-user-form"
                    className="btn-primary"
                    style={{ borderRadius: 10 }}
                  >
                    Update Member
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
          </motion.div>
        )}
      </AnimatePresence>
      {/* Delete Confirmation Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2" style={{ background: 'transparent' }}>
            <ExclamationCircleOutlined className="text-red-500" />
            <span>Confirm Deletion</span>
          </div>
        }
        open={deleteModalVisible}
        onOk={confirmDeleteUser}
        onCancel={cancelDeleteUser}
        confirmLoading={deleteLoading}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        centered
        styles={{
          header: {
            background: 'transparent',
            borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.06)',
          },
          body: {
            background: 'transparent',
          },
          content: {
            background: isDark ? '#1f1f1f' : '#ffffff',
          },
        }}
      >
        <div className="py-4">
          <p className="text-base mb-2" style={{ color: isDark ? '#ffffff' : '#000000' }}>
            Are you sure you want to delete <strong>{userToDelete?.name || 'this user'}</strong>?
          </p>
          <p className="text-sm" style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.45)' }}>
            This action cannot be undone. The user will be deleted and will no longer have access to the system.
          </p>
        </div>
      </Modal>

      {/* Stealth Mode Confirmation Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2" style={{ background: 'transparent' }}>
            <ExclamationCircleOutlined className="text-yellow-500" />
            <span>Confirm Background Mode Change</span>
          </div>
        }
        open={stealthModeModalVisible}
        onOk={confirmStealthModeToggle}
        onCancel={cancelStealthModeToggle}
        confirmLoading={stealthModeLoading}
        okText={userToToggleStealth?.newValue ? "Enable" : "Disable"}
        cancelText="Cancel"
        okButtonProps={{ 
          style: { 
            backgroundColor: userToToggleStealth?.newValue ? '#f59e0b' : '#6b7280',
            borderColor: userToToggleStealth?.newValue ? '#f59e0b' : '#6b7280'
          } 
        }}
        centered
        styles={{
          header: {
            background: 'transparent',
            borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.06)',
          },
          body: {
            background: 'transparent',
          },
          content: {
            background: isDark ? '#1f1f1f' : '#ffffff',
          },
        }}
      >
        <div className="py-4">
          <p className="text-base mb-2" style={{ color: isDark ? '#ffffff' : '#000000' }}>
            Are you sure you want to {userToToggleStealth?.newValue ? 'enable' : 'disable'} Background mode for <strong>{userToToggleStealth?.name || 'this user'}</strong>?
          </p>
          {/* <p className="text-sm" style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.45)' }}>
            {userToToggleStealth?.newValue 
              ? 'Stealth mode will be enabled for this user'
              : 'Stealth mode will be disabled for this user'
            }
          </p> */}
        </div>
      </Modal>

      {/* Bulk Stealth Mode Confirmation Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2" style={{ background: 'transparent' }}>
            <ExclamationCircleOutlined className="text-yellow-500" />
            <span>Confirm Bulk Background Mode Change</span>
          </div>
        }
        open={bulkStealthModeModalVisible}
        onOk={confirmBulkStealthModeToggle}
        onCancel={cancelBulkStealthModeToggle}
        confirmLoading={bulkStealthModeLoading}
        okText={bulkStealthModeValue ? "Enable for All" : "Disable for All"}
        cancelText="Cancel"
        okButtonProps={{ 
          style: { 
            backgroundColor: bulkStealthModeValue ? '#f59e0b' : '#6b7280',
            borderColor: bulkStealthModeValue ? '#f59e0b' : '#6b7280'
          } 
        }}
        centered
        styles={{
          header: {
            background: 'transparent',
            borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.06)',
          },
          body: {
            background: 'transparent',
          },
          content: {
            background: isDark ? '#1f1f1f' : '#ffffff',
          },
        }}
      >
        <div className="py-4">
          <p className="text-base mb-2" style={{ color: isDark ? '#ffffff' : '#000000' }}>
            Are you sure you want to {bulkStealthModeValue ? 'enable' : 'disable'} background mode for all members in your organization?
          </p>
          <p className="text-sm mt-3" style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.45)' }}>
            This action will {bulkStealthModeValue ? 'enable' : 'disable'} background mode for all team members at once.
          </p>
        </div>
      </Modal>
    </div>
    </ConfigProvider>
    </PageAnimationWrapper>
  );
};

export default Team;
